import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FiPlus, FiTrash2, FiEdit2, FiRefreshCw, FiTarget, FiSearch, FiX, FiSave, FiChevronDown, FiChevronUp, FiAlertTriangle, FiCopy, FiFolder, FiTrendingUp, FiUser, FiCheckSquare, FiHelpCircle, FiRotateCcw, FiCheck, FiLayers, FiSliders, FiFilter, FiMapPin } from 'react-icons/fi';
import { faturamentoService, filterDataService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Metas.css';
import { BIMultiSelectSearch, BIEmployeeSelectSearch, BIPagination } from '../common';
import Loading from '../common/Loading';

const MESES = [
  { v: 1, n: 'Janeiro' },
  { v: 2, n: 'Fevereiro' },
  { v: 3, n: 'Março' },
  { v: 4, n: 'Abril' },
  { v: 5, n: 'Maio' },
  { v: 6, n: 'Junho' },
  { v: 7, n: 'Julho' },
  { v: 8, n: 'Agosto' },
  { v: 9, n: 'Setembro' },
  { v: 10, n: 'Outubro' },
  { v: 11, n: 'Novembro' },
  { v: 12, n: 'Dezembro' }
];

// ==========================================================================
// Componente 1: Select com Busca de Vendedor (Padrão BI)
// ==========================================================================
const BIVendorSelectSearch = ({ label = 'Vendedor', value, onChange, required }) => {
  const containerRef = useRef(null);
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await filterDataService.getVendedores('', 10000, 0);
      setOptions(res?.data?.vendedores || []);
    } catch (err) {
      console.error('Erro ao carregar vendedores:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const selectedVendor = useMemo(() => {
    if (!value) return null;
    return options.find((v) => String(v.id) === String(value) || String(v.codigo) === String(value)) || null;
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const q = searchTerm.toLowerCase().trim();
    return options.filter((v) => {
      const nome = String(v.nome || '').toLowerCase();
      const cod = String(v.codigo || '').toLowerCase();
      const id = String(v.id || '').toLowerCase();
      const fil = String(v.codfilial || '').toLowerCase();
      return nome.includes(q) || cod.includes(q) || id.includes(q) || fil.includes(q);
    });
  }, [options, searchTerm]);

  const displayLabel = selectedVendor
    ? `#${selectedVendor.codigo || selectedVendor.id} - ${selectedVendor.nome}${selectedVendor.codfilial ? ` • Filial ${selectedVendor.codfilial}` : ''}`
    : value ? (loading ? 'Carregando vendedor...' : `Vendedor #${value}`) : '';

  const handleSelect = (v) => {
    onChange(String(v.id));
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    if (e) e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className="form-group-modern mb-3" style={{ position: 'relative', width: '100%', zIndex: isOpen ? 1055 : 1 }} ref={containerRef}>
      <label className="d-flex justify-content-between align-items-center">
        <span>
          <FiUser className="me-1 text-primary" />
          {label} {required && <span className="text-danger">*</span>}
        </span>
      </label>

      <div className="bi-search-dropdown-container">
        <div className="bi-search-input-wrapper">
          <input
            type="text"
            className={`form-control-modern bi-search-input-box ${value ? 'is-selected' : ''}`}
            placeholder={isOpen ? 'Buscar vendedor por nome, RCA ou código...' : (displayLabel || 'Selecione o vendedor...')}
            value={isOpen ? searchTerm : displayLabel}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onClick={() => {
              if (!isOpen) setIsOpen(true);
            }}
            readOnly={!isOpen}
            required={required && !value}
          />

          <div className="bi-search-actions-inside">
            {value && (
              <span className="bi-selected-badge vendor" title="Vendedor selecionado">
                <FiCheckSquare size={12} /> Vendedor
              </span>
            )}
            {(value || (isOpen && searchTerm)) && (
              <span
                className="bi-search-clear-icon"
                title="Limpar seleção"
                onClick={handleClear}
              >
                <FiX size={15} />
              </span>
            )}
            <span
              className={`bi-search-chevron ${isOpen ? 'open' : ''}`}
              onClick={() => setIsOpen(!isOpen)}
              title={isOpen ? 'Fechar lista' : 'Abrir lista'}
            >
              <FiChevronDown size={16} />
            </span>
          </div>
        </div>

        {/* Modal Suspenso (Popover) */}
        {isOpen && (
          <div className="bi-search-popover">
            <div className="bi-popover-header">
              <span className="bi-popover-meta-title">
                <FiUser size={13} className="text-primary me-1" />
                Vendedores Disponíveis
              </span>
              <div className="bi-popover-header-actions">
                {value && (
                  <button
                    type="button"
                    className="bi-popover-clear-btn"
                    onClick={handleClear}
                  >
                    Desmarcar
                  </button>
                )}
                <span className="bi-popover-count-tag">
                  {filteredOptions.length} / {options.length}
                </span>
              </div>
            </div>

            <div className="bi-popover-list">
              {loading ? (
                <div className="bi-popover-empty">
                  <FiRefreshCw size={20} className="spin-ico d-block mx-auto mb-2 text-primary" />
                  Carregando vendedores...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="bi-popover-empty">
                  <FiHelpCircle size={22} className="d-block mx-auto mb-1 opacity-50" />
                  Nenhum vendedor encontrado{searchTerm ? ` para "${searchTerm}"` : ''}.
                </div>
              ) : (
                filteredOptions.map((v) => {
                  const isSelected = String(v.id) === String(value) || String(v.codigo) === String(value);
                  const initials = (v.nome || 'VD').substring(0, 2).toUpperCase();

                  return (
                    <div
                      key={v.id}
                      className={`bi-popover-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelect(v)}
                    >
                      <div className="bi-popover-item-left">
                        <div className="bi-popover-avatar vendor">
                          {initials}
                        </div>
                        <div className="bi-popover-info">
                          <span className="bi-popover-name" title={v.nome}>
                            {v.nome}
                          </span>
                          <div className="bi-popover-meta">
                            <span>RCA: <strong>#{v.codigo}</strong></span>
                            {v.codfilial && <span>• Filial {v.codfilial}</span>}
                            <span className="bi-popover-tag vendedor">Vendedor</span>
                          </div>
                        </div>
                      </div>

                      <div className="bi-popover-item-right">
                        {isSelected ? (
                          <span className="text-primary d-flex align-items-center gap-1 fw-bold" style={{ fontSize: '0.78rem' }}>
                            <FiCheckSquare size={16} /> Selecionado
                          </span>
                        ) : (
                          <span className="bi-popover-target-val">Cód. {v.codigo}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ==========================================================================
// Componente 3: Select com Busca de Grupo de Metas (Padrão BI)
// ==========================================================================
const BIGoalGroupSelectSearch = ({
  label = 'Grupo de Metas',
  value,
  onChange,
  onClear,
  presets = [],
  required = true,
  currentVendorId,
  currentEmployeeId,
  vendorCodusurMap = {},
  getGoalClassificationLabel = (val) => val || 'Vendedor',
  getGoalSubtypeLabel = (val) => val || ''
}) => {
  const containerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const selectedPreset = useMemo(() => {
    if (!value) return null;
    return presets.find((p) => String(p.id) === String(value)) || null;
  }, [presets, value]);

  const filteredPresets = useMemo(() => {
    const vId = currentVendorId && Number(currentVendorId) > 0 ? Number(currentVendorId) : null;
    const vCod = (vId && vendorCodusurMap && vendorCodusurMap[String(vId)]) ? Number(vendorCodusurMap[String(vId)]) : null;
    const eId = currentEmployeeId && Number(currentEmployeeId) > 0 ? Number(currentEmployeeId) : null;

    let list = presets;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = presets.filter((p) => {
        const name = String(p.name || '').toLowerCase();
        const classif = String(p.goal_classification || '').toLowerCase();
        const classifLabel = String(getGoalClassificationLabel(p.goal_classification) || '').toLowerCase();
        const subtype = String(p.goal_subtype || '').toLowerCase();
        const subtypeLabel = String(getGoalSubtypeLabel ? getGoalSubtypeLabel(p.goal_subtype) : '').toLowerCase();
        return name.includes(q) || classif.includes(q) || classifLabel.includes(q) || subtype.includes(q) || subtypeLabel.includes(q);
      });
    }

    return [...list].sort((a, b) => {
      const getPriority = (p) => {
        const vArr = (p.vendor_ids_array || []).map(Number);
        const eArr = (p.employee_ids_array || []).map(Number);
        if (vId && vArr.length > 0) {
          if (vArr.includes(vId) || (vCod != null && vArr.includes(vCod))) return 0;
          return 2;
        }
        if (eId && eArr.length > 0) {
          if (eArr.includes(eId)) return 0;
          return 2;
        }
        if (vArr.length > 0 || eArr.length > 0) return 2;
        return 1;
      };
      const prioA = getPriority(a);
      const prioB = getPriority(b);
      if (prioA !== prioB) return prioA - prioB;
      return 0;
    });
  }, [presets, searchTerm, currentVendorId, currentEmployeeId, vendorCodusurMap, getGoalClassificationLabel, getGoalSubtypeLabel]);

  const displayLabel = selectedPreset
    ? `${selectedPreset.name} (${getGoalClassificationLabel(selectedPreset.goal_classification)}${selectedPreset.goal_subtype ? ` • ${getGoalSubtypeLabel ? getGoalSubtypeLabel(selectedPreset.goal_subtype) : selectedPreset.goal_subtype}` : ''} | ${selectedPreset.filter_ids_array?.length || 0} filtros, ${selectedPreset.variable_ids_array?.length || 0} variáveis)${selectedPreset.is_default ? ' ★ Padrão' : ''}`
    : '';

  const handleSelect = (p) => {
    onChange(p);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    if (e) e.stopPropagation();
    if (onClear) {
      onClear();
    } else {
      onChange('');
    }
    setSearchTerm('');
  };

  return (
    <div className="form-group-modern mb-3" style={{ position: 'relative', width: '100%', zIndex: isOpen ? 1053 : 1 }} ref={containerRef}>
      <label className="d-flex justify-content-between align-items-center">
        <span>
          <FiFolder className="me-1 text-primary" />
          {label} {required && <span className="text-danger">*</span>}
        </span>
        {value && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none text-muted"
            style={{ fontSize: '0.75rem' }}
            onClick={handleClear}
          >
            Trocar grupo
          </button>
        )}
      </label>

      <div className="bi-search-dropdown-container">
        <div className="bi-search-input-wrapper">
          <input
            type="text"
            className={`form-control-modern bi-search-input-box ${value ? 'is-selected' : ''}`}
            placeholder={isOpen ? 'Buscar grupo por nome ou classificação...' : (displayLabel || 'Selecione o Grupo de Metas obrigatório...')}
            value={isOpen ? searchTerm : displayLabel}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onClick={() => {
              if (!isOpen) setIsOpen(true);
            }}
            readOnly={!isOpen}
            required={required && !value}
          />

          <div className="bi-search-actions-inside">
            {value && (
              <span className="bi-selected-badge preset" title="Grupo de Metas selecionado">
                <FiCheckSquare size={12} /> Grupo
              </span>
            )}
            {(value || (isOpen && searchTerm)) && (
              <span
                className="bi-search-clear-icon"
                title="Limpar seleção de grupo"
                onClick={handleClear}
              >
                <FiX size={15} />
              </span>
            )}
            <span
              className={`bi-search-chevron ${isOpen ? 'open' : ''}`}
              onClick={() => setIsOpen(!isOpen)}
              title={isOpen ? 'Fechar lista' : 'Abrir lista'}
            >
              <FiChevronDown size={16} />
            </span>
          </div>
        </div>

        {/* Modal Suspenso (Popover) */}
        {isOpen && (
          <div className="bi-search-popover">
            <div className="bi-popover-header">
              <span className="bi-popover-meta-title">
                <FiFolder size={13} className="text-warning me-1" />
                Grupos de Metas Oficiais
              </span>
              <div className="bi-popover-header-actions">
                {value && (
                  <button
                    type="button"
                    className="bi-popover-clear-btn"
                    onClick={handleClear}
                  >
                    Desmarcar
                  </button>
                )}
                <span className="bi-popover-count-tag">
                  {filteredPresets.length} / {presets.length}
                </span>
              </div>
            </div>

            <div className="bi-popover-list">
              {filteredPresets.length === 0 ? (
                <div className="bi-popover-empty">
                  <FiHelpCircle size={22} className="d-block mx-auto mb-1 opacity-50" />
                  Nenhum grupo de metas encontrado{searchTerm ? ` para "${searchTerm}"` : ''}.
                </div>
              ) : (
                filteredPresets.map((gp) => {
                  const isSelected = String(gp.id) === String(value);
                  const classif = gp.goal_classification || 'vendedor';
                  const classifTagClass = classif === 'bijou' ? 'bijou' : (classif === 'operador_caixa' ? 'caixa' : 'vendedor');

                  return (
                    <div
                      key={gp.id}
                      className={`bi-popover-item ${isSelected ? 'selected-preset' : ''}`}
                      onClick={() => handleSelect(gp)}
                    >
                      <div className="bi-popover-item-left">
                        <div className="bi-popover-avatar preset">
                          <FiFolder size={15} color="#ffffff" />
                        </div>
                        <div className="bi-popover-info">
                          <span className="bi-popover-name" title={gp.name}>
                            {gp.name}
                          </span>
                          <div className="bi-popover-meta">
                            <span className={`bi-popover-tag ${classifTagClass}`}>
                              {getGoalClassificationLabel(classif)}
                            </span>
                            {gp.goal_subtype ? (
                              <span className="badge px-1 py-0" style={{
                                fontSize: '0.65rem',
                                backgroundColor: gp.goal_subtype === 'clientes_positivados' ? '#f0f9ff' : '#f5f3ff',
                                color: gp.goal_subtype === 'clientes_positivados' ? '#0284c7' : '#7c3aed',
                                border: `1px solid ${gp.goal_subtype === 'clientes_positivados' ? '#bae6fd' : '#ddd6fe'}`
                              }}>
                                {getGoalSubtypeLabel ? getGoalSubtypeLabel(gp.goal_subtype) : gp.goal_subtype}
                              </span>
                            ) : null}
                            <span>• {gp.filter_ids_array?.length || 0} filtros</span>
                            <span>• {gp.variable_ids_array?.length || 0} variáveis</span>
                            {gp.is_default ? (
                              <span className="badge bg-warning text-dark px-1 py-0" style={{ fontSize: '0.65rem' }}>
                                ★ Padrão
                              </span>
                            ) : null}
                            {(() => {
                              const vId = currentVendorId && Number(currentVendorId) > 0 ? Number(currentVendorId) : null;
                              const vCod = (vId && vendorCodusurMap && vendorCodusurMap[String(vId)]) ? Number(vendorCodusurMap[String(vId)]) : null;
                              const eId = currentEmployeeId && Number(currentEmployeeId) > 0 ? Number(currentEmployeeId) : null;
                              const vArr = (gp.vendor_ids_array || []).map(Number);
                              const eArr = (gp.employee_ids_array || []).map(Number);
                              const isBelongingToMe = (vId && (vArr.includes(vId) || (vCod != null && vArr.includes(vCod)))) ||
                                                      (eId && eArr.includes(eId));
                              const isBelongingToOther = (vArr.length > 0 || eArr.length > 0) && !isBelongingToMe;

                              if (isBelongingToMe) {
                                return (
                                  <span className="badge bg-success text-white px-1 py-0" style={{ fontSize: '0.65rem' }}>
                                    ★ Deste Vendedor
                                  </span>
                                );
                              }
                              if (isBelongingToOther) {
                                return (
                                  <span className="badge bg-secondary text-white px-1 py-0 opacity-75" style={{ fontSize: '0.65rem' }}>
                                    Outro vendedor/func.
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      </div>

                      <div className="bi-popover-item-right">
                        {isSelected ? (
                          <span className="d-flex align-items-center gap-1 fw-bold" style={{ fontSize: '0.78rem', color: '#d97706' }}>
                            <FiCheckSquare size={16} /> Selecionado
                          </span>
                        ) : (
                          <span className="bi-popover-target-val">ID #{gp.id}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MultiSelectSearch = BIMultiSelectSearch;

const ProductMultiSelect = ({ label, value, onChange }) => (
  <BIMultiSelectSearch
    label={label}
    placeholder="Buscar produto..."
    value={value}
    onChange={onChange}
    fetchData={(search, limit) => faturamentoService.listProducts({ search, per_page: limit || 200 })}
    badgeColor="#8b5cf6"
    formatLabel={(opt) => `${opt.codprod || opt.codigo} - ${opt.descricao}`}
    getOptionValue={(opt) => opt.codprod || opt.codigo || opt.id}
  />
);

const getDefaultFilterPeriod = () => {
  const now = new Date();
  const today = now.getDate();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  if (today <= 5) {
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    return { month: prevMonth, year: prevYear };
  }
  return { month, year };
};

const formatCurrencyBRL = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  const padded = digits.padStart(3, '0');
  const intPart = padded.slice(0, -2).replace(/^0+/, '') || '0';
  const decPart = padded.slice(-2);
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formattedInt},${decPart}`;
};

const parseCurrencyBRL = (formatted) => {
  if (!formatted) return '';
  return formatted.replace(/\./g, '').replace(',', '.');
};

const parseBranchCsv = (csv) => {
  if (!csv || String(csv).trim() === '') return [];
  return String(csv).split(',').map(s => s.trim()).filter(Boolean);
};

function Metas() {
  const { hasSectionAccess } = useAuth();
  const canManage = hasSectionAccess('metas', 'manage');
  const [goals, setGoals] = useState([]);
  const [filters, setFilters] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const defaultFilterPeriod = useMemo(() => getDefaultFilterPeriod(), []);
  const [filterYear, setFilterYear] = useState(String(defaultFilterPeriod.year));
  const [filterMonth, setFilterMonth] = useState(String(defaultFilterPeriod.month));
  const [filterBranchSearch, setFilterBranchSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterClassification, setFilterClassification] = useState('');
  const [vendorBranchMap, setVendorBranchMap] = useState({});
  const [vendorCodusurMap, setVendorCodusurMap] = useState({});
  const [vendorCodusurToIdMap, setVendorCodusurToIdMap] = useState({});
  const [employeeBranchMap, setEmployeeBranchMap] = useState({});
  const [employeeCodfuncToIdMap, setEmployeeCodfuncToIdMap] = useState({}); // codfunc (Winthor) → id (banco)
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState('period');
  const [sortOrder, setSortOrder] = useState('desc');
  const [goalPresets, setGoalPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [suggestedPreset, setSuggestedPreset] = useState(null);

  const handleSort = (field) => {
    let newOrder = 'asc';
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newOrder = (field === 'period' || field === 'value' || field === 'id') ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortOrder(newOrder);
    setPage(1);
    loadGoals({ page: 1, per_page: perPage, sort_by: field, sort_order: newOrder });
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: 4, opacity: 0.25, fontSize: 10, userSelect: 'none' }}>▲▼</span>;
    return <span style={{ marginLeft: 4, fontSize: 10, userSelect: 'none' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  // Estados para modais de confirmação
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorModalTitle, setErrorModalTitle] = useState('Erro ao Salvar');

  // Estados para edicao inline de validacao de filtros e variaveis
  const [editingFilterId, setEditingFilterId] = useState(null);
  const [editingVariableId, setEditingVariableId] = useState(null);
  const [filterEditData, setFilterEditData] = useState({});
  const [variableEditData, setVariableEditData] = useState({});
  const [savingFilterValid, setSavingFilterValid] = useState(false);
  const [savingVariable, setSavingVariable] = useState(false);

  // Suporte a múltipla edição simultânea
  const [editingVariableIds, setEditingVariableIds] = useState([]);
  const [variablesEditData, setVariablesEditData] = useState({});
  const [editingFilterIds, setEditingFilterIds] = useState([]);
  const [filtersEditData, setFiltersEditData] = useState({});

  // Modal para confirmar salvamento do filtro: exclusivo da meta vs atualizar filtro base
  const [filterConfirmModal, setFilterConfirmModal] = useState({ open: false, filter: null, saveData: null, isBatch: false, batchItems: [] });

  // Estados para edição inline de regras de acréscimo de variáveis
  const [editingAccrualVarId, setEditingAccrualVarId] = useState(null);
  const [accrualEditRules, setAccrualEditRules] = useState([]);
  const [savingAccrualRules, setSavingAccrualRules] = useState(false);
  const [reports, setReports] = useState([]);

  // Estado para Modal de Copiar Referências para outras Metas
  const [copyRefModal, setCopyRefModal] = useState({
    open: false,
    sourceGoal: null,
    targetGoalIds: [],
    loading: false,
    applying: false,
    candidates: [],
    filterYear: '',
    filterMonth: '',
    filterBranch: '',
    searchTerm: '',
    options: {
      copy_bi_references: true,
      copy_filters: true,
      copy_filter_configs: true,
      copy_variables: true,
      copy_bonus_params: true,
      copy_target_value: false,
      copy_notes: false
    }
  });

  const copySearchTimeoutRef = useRef(null);

  const parseVariableIds = (input) => {
    if (Array.isArray(input)) return input;
    if (typeof input === 'string' && input) return input.split(',').map(v => parseInt(v, 10)).filter(v => !isNaN(v));
    return [];
  };

  const [form, setForm] = useState({
    id: null,
    vendor_id: '',
    employee_id: '',
    employee_codfunc: '', // codfunc vindo de preset (não é o id do banco)
    filter_ids: [],
    variable_ids: [],
    goal_type: 'monthly_revenue',
    goal_subtype: '',
    goal_classification: 'vendedor',
    reference_year: new Date().getFullYear(),
    reference_month: new Date().getMonth() + 1,
    target_amount: '',
    target_quantity: '',
    unit_value: '',
    bonus_amount: '',
    product_ids: '',
    notes: '',
    ref_daily_net_sales: '',
    ref_daily_orders_qty: '',
    ref_average_ticket: '',
    ref_items_per_attendance: '',
    ref_item_value_per_attendance: '',
    filter_configs: {}
  });

  const selectedPreset = useMemo(() => {
    if (!selectedPresetId) return null;
    return goalPresets.find(p => String(p.id) === String(selectedPresetId)) || null;
  }, [selectedPresetId, goalPresets]);

  const presetFilterNames = useMemo(() => {
    if (selectedPreset) {
      const ids = selectedPreset.filter_ids_array || [];
      return ids.map(id => {
        const f = filters.find(flt => Number(flt.id) === Number(id));
        return f ? f.name : `Filtro #${id}`;
      });
    }
    if (form.filter_ids && form.filter_ids.length > 0) {
      return form.filter_ids.map(id => {
        const f = filters.find(flt => Number(flt.id) === Number(id));
        return f ? f.name : `Filtro #${id}`;
      });
    }
    return [];
  }, [selectedPreset, form.filter_ids, filters]);

  const presetVariableNames = useMemo(() => {
    if (selectedPreset) {
      const ids = selectedPreset.variable_ids_array || [];
      return ids.map(id => {
        const v = variables.find(vr => Number(vr.id) === Number(id));
        return v ? v.name : `Variável #${id}`;
      });
    }
    if (form.variable_ids && form.variable_ids.length > 0) {
      return form.variable_ids.map(id => {
        const v = variables.find(vr => Number(vr.id) === Number(id));
        return v ? v.name : `Variável #${id}`;
      });
    }
    return [];
  }, [selectedPreset, form.variable_ids, variables]);

  const loadGoals = useCallback(async (customParams = {}) => {
    setLoading(true);
    try {
      const activePage = customParams.page !== undefined ? customParams.page : page;
      const activePerPage = customParams.per_page !== undefined ? customParams.per_page : perPage;
      const activeSortBy = customParams.sort_by !== undefined ? customParams.sort_by : sortField;
      const activeSortOrder = customParams.sort_order !== undefined ? customParams.sort_order : sortOrder;

      const activeSearch = customParams.search !== undefined ? customParams.search : debouncedSearch;
      const activeYear = customParams.year !== undefined ? customParams.year : filterYear;
      const activeMonth = customParams.month !== undefined ? customParams.month : filterMonth;
      const activeBranch = customParams.filter_branch !== undefined
        ? customParams.filter_branch
        : (customParams.branch !== undefined ? customParams.branch : filterBranchSearch);
      const activeType = customParams.goal_type !== undefined ? customParams.goal_type : filterType;
      const activeClassification = customParams.goal_classification !== undefined ? customParams.goal_classification : filterClassification;

      const query = {
        page: activePage,
        per_page: activePerPage,
        sort_by: activeSortBy,
        sort_order: activeSortOrder
      };

      if (activeSearch && String(activeSearch).trim() !== '') {
        query.search = String(activeSearch).trim();
      }
      if (activeYear !== null && activeYear !== '') {
        query.year = activeYear;
      }
      if (activeMonth !== null && activeMonth !== '') {
        query.month = activeMonth;
      }
      if (activeBranch !== null && activeBranch !== '') {
        query.filter_branch = activeBranch;
      }
      if (activeType !== null && activeType !== '') {
        query.goal_type = activeType;
      }
      if (activeClassification !== null && activeClassification !== '') {
        query.goal_classification = activeClassification;
      }

      const res = await faturamentoService.listGoals(query);
      setGoals(res?.data?.goals || []);
      setTotalRecords(res?.data?.total || 0);
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao listar metas' });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortField, sortOrder, debouncedSearch, filterYear, filterMonth, filterBranchSearch, filterType, filterClassification]);

  // Carregamento de metas sempre que paginação, ordenação ou qualquer filtro mudar
  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const loadFilters = useCallback(async () => {
    try {
      const res = await faturamentoService.listSavedFilters({ active_only: 1 });
      setFilters(res?.data?.filters || []);
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao listar filtros salvos' });
    }
  }, []);

  const loadVariables = useCallback(async () => {
    try {
      const res = await faturamentoService.listVariables({ per_page: 0 });
      setVariables(res?.data?.variables || []);
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao listar variáveis' });
    }
  }, []);

  const loadVendorBranchMap = useCallback(async () => {
    try {
      const res = await filterDataService.getVendedores('', 10000, 0);
      const vendors = res?.data?.vendedores || [];
      const branchMap = {};
      const codusurMap = {};
      const codusurToIdMap = {};
      vendors.forEach(v => {
        branchMap[String(v.id)] = v.codfilial != null ? String(v.codfilial) : '';
        if (v.codigo != null && String(v.codigo).trim() !== '') {
          codusurMap[String(v.id)] = String(v.codigo);
          codusurToIdMap[String(v.codigo)] = String(v.id);
        }
      });
      setVendorBranchMap(branchMap);
      setVendorCodusurMap(codusurMap);
      setVendorCodusurToIdMap(codusurToIdMap);
    } catch (e) {
      console.error('Erro ao carregar vendedores para mapa de filiais:', e);
    }
  }, []);

  const loadEmployeeBranchMap = useCallback(async () => {
    try {
      const res = await faturamentoService.listEmployees({ per_page: 10000 });
      const employees = res?.data?.employees || [];
      const branchMap = {};
      const codfuncMap = {}; // codfunc (código Winthor) → id (auto-increment do banco)
      employees.forEach(e => {
        branchMap[String(e.id)] = e.codfilial != null ? String(e.codfilial) : '';
        if (e.codfunc != null && String(e.codfunc).trim() !== '') {
          codfuncMap[String(e.codfunc)] = String(e.id);
        }
        if (e.matricula != null && String(e.matricula).trim() !== '') {
          codfuncMap[String(e.matricula)] = String(e.id);
        }
      });
      setEmployeeBranchMap(branchMap);
      setEmployeeCodfuncToIdMap(codfuncMap);
    } catch (e) {
      console.error('Erro ao carregar funcionários para mapa de filiais:', e);
    }
  }, []);

  const loadPresets = useCallback(async () => {
    try {
      const res = await faturamentoService.listGoalPresets({ active: 1, per_page: 0 });
      setGoalPresets(res?.data?.presets || []);
    } catch (e) {
      console.error('Erro ao carregar grupos padrão de metas:', e);
    }
  }, []);

  const loadReports = useCallback(async () => {
    try {
      const res = await faturamentoService.listReports({ per_page: 200 });
      setReports(res?.data?.reports || []);
    } catch (e) {
      console.error('Erro ao carregar relatórios:', e);
    }
  }, []);

  useEffect(() => {
    loadFilters();
    loadVariables();
    loadVendorBranchMap();
    loadEmployeeBranchMap();
    loadPresets();
    loadReports();
  }, [loadFilters, loadVariables, loadVendorBranchMap, loadEmployeeBranchMap, loadPresets, loadReports]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const totalPages = Math.ceil(totalRecords / perPage) || 1;

  const resetForm = () => {
    setForm({
      id: null,
      vendor_id: '',
      employee_id: '',
      filter_ids: [],
      variable_ids: [],
      goal_type: 'monthly_revenue',
      goal_subtype: '',
      goal_classification: 'vendedor',
      reference_year: new Date().getFullYear(),
      reference_month: new Date().getMonth() + 1,
      target_amount: '',
      target_quantity: '',
      unit_value: '',
      bonus_amount: '',
      product_ids: '',
      notes: '',
      ref_daily_net_sales: '',
      ref_daily_orders_qty: '',
      ref_average_ticket: '',
      ref_items_per_attendance: '',
      ref_item_value_per_attendance: '',
      filter_configs: {}
    });
    setEditingVariableIds([]);
    setVariablesEditData({});
    setEditingFilterIds([]);
    setFiltersEditData({});
    setEditingVariableId(null);
    setEditingFilterId(null);
  };

  const isQuantitySubtype = (subtype) => ['clientes_positivados', 'produtos_destaque'].includes(subtype);

  const formatInitialAmount = (value) => {
    if (!value && value !== 0) return '';
    return formatCurrencyBRL(String(Math.round(Number(value) * 100)));
  };

  const handleApplyPreset = (presetIdOrObj) => {
    const preset = typeof presetIdOrObj === 'object' && presetIdOrObj !== null
      ? presetIdOrObj
      : goalPresets.find(p => String(p.id) === String(presetIdOrObj));
    
    if (!preset) {
      setSelectedPresetId('');
      return;
    }

    setSelectedPresetId(String(preset.id));
    setSuggestedPreset(null);

    const presetFilterIds = preset.filter_ids_array || [];
    const presetVarIds = preset.variable_ids_array || [];

    // Vendedor padrão: prioriza default_vendor_id resolvido pelo backend
    const rawPresetVendorId = (preset.vendor_ids_array && preset.vendor_ids_array.length > 0)
      ? String(preset.vendor_ids_array[0])
      : (preset.vendor_ids ? String(preset.vendor_ids).split(',')[0].trim() : '');
    const presetVendorId = preset.default_vendor_id != null
      ? String(preset.default_vendor_id)
      : (rawPresetVendorId ? (vendorCodusurToIdMap[rawPresetVendorId] || rawPresetVendorId) : '');

    // Funcionário padrão: prioriza default_employee_id resolvido pelo backend
    const rawPresetEmployeeCodfunc = (preset.employee_ids_array && preset.employee_ids_array.length > 0)
      ? String(preset.employee_ids_array[0])
      : (preset.employee_ids ? String(preset.employee_ids).split(',')[0].trim() : '');

    const resolvedEmployeeId = preset.default_employee_id != null
      ? String(preset.default_employee_id)
      : (rawPresetEmployeeCodfunc
        ? (employeeCodfuncToIdMap[rawPresetEmployeeCodfunc] || rawPresetEmployeeCodfunc)
        : '');

    const resolvedEmployeeCodfunc = preset.default_employee_codfunc != null
      ? String(preset.default_employee_codfunc)
      : rawPresetEmployeeCodfunc;

    const presetClassification = preset.goal_classification || 'vendedor';
    const presetSubtype = preset.goal_subtype || '';

    const isEmployeePreset = preset.target_type === 'employee' || Boolean(resolvedEmployeeId && Number(resolvedEmployeeId) > 0);

    setForm(prev => {
      const next = {
        ...prev,
        filter_ids: presetFilterIds,
        variable_ids: presetVarIds,
        goal_classification: presetClassification,
        goal_subtype: presetSubtype,
        employee_codfunc: resolvedEmployeeCodfunc
      };

      if (isEmployeePreset && resolvedEmployeeId && Number(resolvedEmployeeId) > 0) {
        // Usa id real do banco e limpa vendedor para meta de funcionário
        next.employee_id = resolvedEmployeeId;
        next.vendor_id = '';
      } else if (presetVendorId && Number(presetVendorId) > 0) {
        next.vendor_id = presetVendorId;
        next.employee_id = '';
        next.employee_codfunc = '';
      }

      return next;
    });

    const extraMsgs = [];
    if (presetVendorId && Number(presetVendorId) > 0 && !isEmployeePreset) {
      extraMsgs.push(preset.default_vendor_name ? `vendedor: ${preset.default_vendor_name}` : 'vendedor');
    }
    if (resolvedEmployeeId && Number(resolvedEmployeeId) > 0) {
      extraMsgs.push(preset.default_employee_name ? `funcionário: ${preset.default_employee_name}` : 'funcionário');
    }
    if (presetClassification) extraMsgs.push(`classificação: ${getGoalClassificationLabel(presetClassification)}`);
    if (presetSubtype) extraMsgs.push(`subtipo: ${getGoalSubtypeLabel(presetSubtype)}`);
    const extraText = extraMsgs.length > 0 ? ` e ${extraMsgs.join(' | ')} padrão preenchido(s)` : '';

    setMessage({
      type: 'info',
      text: `Grupo de metas "${preset.name}" aplicado! (${presetFilterIds.length} filtros, ${presetVarIds.length} variáveis${extraText})`
    });
  };

  const handleVendorChange = async (vId) => {
    const cleanId = vId && Number(vId) > 0 ? String(vId) : '';
    setForm(prev => ({
      ...prev,
      vendor_id: cleanId,
      employee_id: cleanId ? '' : prev.employee_id,
      employee_codfunc: cleanId ? '' : prev.employee_codfunc
    }));
    if (!cleanId) {
      setSuggestedPreset(null);
      return;
    }

    try {
      const branchCsv = vendorBranchMap[String(cleanId)];
      const branchCode = parseBranchCsv(branchCsv)[0] || '';
      const res = await faturamentoService.resolveGoalPreset({
        vendor_id: cleanId,
        branch_code: branchCode,
        goal_classification: form.goal_classification || 'vendedor',
        goal_subtype: form.goal_subtype || ''
      });

      if (res?.data?.found && res?.data?.preset) {
        const resolved = res.data.preset;
        if (!form.id && (!form.filter_ids || form.filter_ids.length === 0) && (!form.variable_ids || form.variable_ids.length === 0)) {
          handleApplyPreset(resolved);
        } else {
          setSuggestedPreset(resolved);
        }
      } else {
        setSuggestedPreset(null);
      }
    } catch (e) {
      console.error('Erro ao resolver padrão de metas para o vendedor:', e);
    }
  };

  const handleEmployeeChange = async (eId) => {
    const cleanId = eId && Number(eId) > 0 ? String(eId) : '';
    setForm(prev => ({
      ...prev,
      employee_id: cleanId,
      employee_codfunc: '',
      vendor_id: cleanId ? '' : prev.vendor_id
    }));
    if (!cleanId) {
      setSuggestedPreset(null);
      return;
    }

    try {
      const branchCsv = employeeBranchMap[String(cleanId)];
      const branchCode = parseBranchCsv(branchCsv)[0] || '';
      const res = await faturamentoService.resolveGoalPreset({
        employee_id: cleanId,
        branch_code: branchCode,
        goal_classification: form.goal_classification || 'vendedor',
        goal_subtype: form.goal_subtype || ''
      });

      if (res?.data?.found && res?.data?.preset) {
        const resolved = res.data.preset;
        if (!form.id && (!form.filter_ids || form.filter_ids.length === 0) && (!form.variable_ids || form.variable_ids.length === 0)) {
          handleApplyPreset(resolved);
        } else {
          setSuggestedPreset(resolved);
        }
      } else {
        setSuggestedPreset(null);
      }
    } catch (e) {
      console.error('Erro ao resolver padrão de metas para funcionário:', e);
    }
  };

  const handleClassificationChange = async (newClassification) => {
    setForm(prev => ({ ...prev, goal_classification: newClassification }));
    const vendorId = form.vendor_id;
    const employeeId = form.employee_id;
    if (!vendorId && !employeeId) {
      return;
    }

    try {
      let branchCode = '';
      if (vendorId) {
        const branchCsv = vendorBranchMap[String(vendorId)];
        branchCode = parseBranchCsv(branchCsv)[0] || '';
      } else if (employeeId) {
        const branchCsv = employeeBranchMap[String(employeeId)];
        branchCode = parseBranchCsv(branchCsv)[0] || '';
      }

      const res = await faturamentoService.resolveGoalPreset({
        vendor_id: vendorId || undefined,
        employee_id: employeeId || undefined,
        branch_code: branchCode || undefined,
        goal_classification: newClassification,
        goal_subtype: form.goal_subtype || undefined
      });

      if (res?.data?.found && res?.data?.preset) {
        const resolved = res.data.preset;
        if (!form.id && (!form.filter_ids || form.filter_ids.length === 0) && (!form.variable_ids || form.variable_ids.length === 0)) {
          handleApplyPreset(resolved);
        } else {
          setSuggestedPreset(resolved);
        }
      } else {
        setSuggestedPreset(null);
      }
    } catch (e) {
      console.error('Erro ao resolver padrão de metas para a classificação:', e);
    }
  };

  const handleSubtypeChange = async (newSubtype) => {
    setForm(prev => ({ ...prev, goal_subtype: newSubtype }));
    const vendorId = form.vendor_id;
    const employeeId = form.employee_id;
    if (!vendorId && !employeeId) {
      return;
    }

    try {
      let branchCode = '';
      if (vendorId) {
        const branchCsv = vendorBranchMap[String(vendorId)];
        branchCode = parseBranchCsv(branchCsv)[0] || '';
      } else if (employeeId) {
        const branchCsv = employeeBranchMap[String(employeeId)];
        branchCode = parseBranchCsv(branchCsv)[0] || '';
      }

      const res = await faturamentoService.resolveGoalPreset({
        vendor_id: vendorId || undefined,
        employee_id: employeeId || undefined,
        branch_code: branchCode || undefined,
        goal_classification: form.goal_classification || 'vendedor',
        goal_subtype: newSubtype || undefined
      });

      if (res?.data?.found && res?.data?.preset) {
        const resolved = res.data.preset;
        if (!form.id && (!form.filter_ids || form.filter_ids.length === 0) && (!form.variable_ids || form.variable_ids.length === 0)) {
          handleApplyPreset(resolved);
        } else {
          setSuggestedPreset(resolved);
        }
      } else {
        setSuggestedPreset(null);
      }
    } catch (e) {
      console.error('Erro ao resolver padrão de metas para o subtipo:', e);
    }
  };


  const findPresetForGoal = useCallback((g, presetsList) => {
    if (!presetsList || presetsList.length === 0 || !g) return null;
    const presets = presetsList;
    const goalFilterIds = Array.isArray(g.filter_ids) ? g.filter_ids.map(Number) : (g.filter_ids ? [Number(g.filter_ids)] : []);
    const goalVarIds = parseVariableIds(g.variable_ids).map(Number);
    const targetSubtype = g.goal_subtype || '';
    const targetClassif = g.goal_classification || 'vendedor';

    const vId = g.vendor_id && Number(g.vendor_id) > 0 ? Number(g.vendor_id) : null;
    const vCod = (vId && vendorCodusurMap[String(vId)]) ? Number(vendorCodusurMap[String(vId)]) : null;
    const eId = g.employee_id && Number(g.employee_id) > 0 ? Number(g.employee_id) : null;

    // Preset pertence diretamente a este vendedor?
    const presetBelongsToThisVendor = (p) => {
      if (!vId) return false;
      const vArr = (p.vendor_ids_array || []).map(Number);
      if (vArr.length === 0) return false;
      return vArr.includes(vId) || (vCod != null && vArr.includes(vCod));
    };

    // Preset pertence diretamente a este funcionário?
    const presetBelongsToThisEmployee = (p) => {
      if (!eId) return false;
      const eArr = (p.employee_ids_array || []).map(Number);
      if (eArr.length === 0) return false;
      return eArr.includes(eId);
    };

    // Preset é seguro para esta meta (pertence a este usuário ou é genérico sem amarrações a outros)?
    const isPresetSafeForGoal = (p) => {
      const vArr = (p.vendor_ids_array || []).map(Number);
      const eArr = (p.employee_ids_array || []).map(Number);

      // Se preset tem amarração a vendedores, não pode ser aplicado para outro vendedor
      if (vArr.length > 0) {
        if (!vId) return false;
        const matchesMe = vArr.includes(vId) || (vCod != null && vArr.includes(vCod));
        if (!matchesMe) return false;
      }

      // Se preset tem amarração a funcionários, não pode ser aplicado para outro funcionário
      if (eArr.length > 0) {
        if (!eId) return false;
        if (!eArr.includes(eId)) return false;
      }

      // Compatibilidade de tipo
      if (vId && p.target_type === 'employee') return false;
      if (eId && p.target_type === 'vendor') return false;

      return true;
    };

    const matchPresetFiltersAndVars = (p) => {
      const pF = (p.filter_ids_array || []).map(Number);
      const pV = (p.variable_ids_array || []).map(Number);
      return pF.length === goalFilterIds.length && pF.every(id => goalFilterIds.includes(id)) &&
             pV.length === goalVarIds.length && pV.every(id => goalVarIds.includes(id));
    };

    // 1. Prioridade: Se a meta já tem preset_id salvo e ele for seguro (não de outro usuário)
    if (g.preset_id) {
      const byId = presets.find(p => String(p.id) === String(g.preset_id));
      if (byId && isPresetSafeForGoal(byId)) {
        return byId;
      }
    }

    // 2. Prioridade: Presets explicitamente atribuídos a este vendedor
    if (vId) {
      const vendorPresets = presets.filter(presetBelongsToThisVendor);
      if (vendorPresets.length > 0) {
        const exact = vendorPresets.find(p => {
          const sameSubtype = !p.goal_subtype || p.goal_subtype === targetSubtype;
          const sameClassif = !p.goal_classification || p.goal_classification === targetClassif;
          return matchPresetFiltersAndVars(p) && sameSubtype && sameClassif;
        }) || vendorPresets.find(p => {
          const sameSubtype = !p.goal_subtype || p.goal_subtype === targetSubtype;
          return matchPresetFiltersAndVars(p) && sameSubtype;
        }) || vendorPresets.find(matchPresetFiltersAndVars)
           || vendorPresets.find(p => (!p.goal_subtype || p.goal_subtype === targetSubtype) && (!p.goal_classification || p.goal_classification === targetClassif))
           || vendorPresets[0];
        if (exact) return exact;
      }
    }

    // 3. Prioridade: Presets explicitamente atribuídos a este funcionário
    if (eId) {
      const employeePresets = presets.filter(presetBelongsToThisEmployee);
      if (employeePresets.length > 0) {
        const exact = employeePresets.find(p => {
          const sameSubtype = !p.goal_subtype || p.goal_subtype === targetSubtype;
          const sameClassif = !p.goal_classification || p.goal_classification === targetClassif;
          return matchPresetFiltersAndVars(p) && sameSubtype && sameClassif;
        }) || employeePresets.find(p => {
          const sameSubtype = !p.goal_subtype || p.goal_subtype === targetSubtype;
          return matchPresetFiltersAndVars(p) && sameSubtype;
        }) || employeePresets.find(matchPresetFiltersAndVars)
           || employeePresets.find(p => (!p.goal_subtype || p.goal_subtype === targetSubtype) && (!p.goal_classification || p.goal_classification === targetClassif))
           || employeePresets[0];
        if (exact) return exact;
      }
    }

    // 4. Prioridade: Casamento de filtros e variáveis APENAS em presets seguros (genéricos)
    const safePresets = presets.filter(isPresetSafeForGoal);
    const matchedSafe = safePresets.find(p => {
      const sameSubtype = !p.goal_subtype || p.goal_subtype === targetSubtype;
      const sameClassif = !p.goal_classification || p.goal_classification === targetClassif;
      return matchPresetFiltersAndVars(p) && sameSubtype && sameClassif;
    }) || safePresets.find(p => {
      const sameSubtype = !p.goal_subtype || p.goal_subtype === targetSubtype;
      return matchPresetFiltersAndVars(p) && sameSubtype;
    }) || safePresets.find(matchPresetFiltersAndVars);

    return matchedSafe || null;
  }, [vendorCodusurMap]);

  const editRow = (g) => {
    const goalFilterIds = Array.isArray(g.filter_ids) ? g.filter_ids.map(Number) : (g.filter_ids ? [Number(g.filter_ids)] : []);
    const goalVarIds = parseVariableIds(g.variable_ids).map(Number);
    const matchedPreset = findPresetForGoal(g, goalPresets);
    setSelectedPresetId(matchedPreset ? String(matchedPreset.id) : (g.preset_id ? String(g.preset_id) : ''));
    setSuggestedPreset(null);
    setForm({
      id: g.id,
      vendor_id: g.vendor_id && Number(g.vendor_id) > 0 ? String(g.vendor_id) : '',
      employee_id: g.employee_id && Number(g.employee_id) > 0 ? String(g.employee_id) : '',
      employee_codfunc: '', // ao editar, o employee_id vem do banco (id real)
      filter_ids: goalFilterIds,
      variable_ids: goalVarIds,
      goal_type: g.goal_type || 'monthly_revenue',
      goal_subtype: g.goal_subtype || '',
      goal_classification: g.goal_classification || (matchedPreset?.goal_classification || 'vendedor'),
      reference_year: g.reference_year || new Date().getFullYear(),
      reference_month: g.reference_month || new Date().getMonth() + 1,
      target_amount: formatInitialAmount(g.target_amount),
      target_quantity: isQuantitySubtype(g.goal_subtype) ? String(g.target_quantity || '') : '',
      unit_value: g.unit_value ? String(Number(g.unit_value)) : '',
      bonus_amount: g.bonus_amount ? formatInitialAmount(g.bonus_amount) : '',
      product_ids: g.product_ids || '',
      notes: g.notes || '',
      ref_daily_net_sales: formatInitialAmount(g.ref_daily_net_sales),
      ref_daily_orders_qty: g.ref_daily_orders_qty != null ? String(g.ref_daily_orders_qty).replace('.', ',') : '',
      ref_average_ticket: formatInitialAmount(g.ref_average_ticket),
      ref_items_per_attendance: g.ref_items_per_attendance != null ? String(g.ref_items_per_attendance).replace('.', ',') : '',
      ref_item_value_per_attendance: formatInitialAmount(g.ref_item_value_per_attendance),
      filter_configs: g.filter_configs || {}
    });
    setShowModal(true);
  };

  const replicate = (g) => {
    const goalFilterIds = Array.isArray(g.filter_ids) ? g.filter_ids.map(Number) : (g.filter_ids ? [Number(g.filter_ids)] : []);
    const goalVarIds = parseVariableIds(g.variable_ids).map(Number);
    const matchedPreset = findPresetForGoal(g, goalPresets);
    setSelectedPresetId(matchedPreset ? String(matchedPreset.id) : (g.preset_id ? String(g.preset_id) : ''));
    setSuggestedPreset(null);
    setForm({
      id: null,
      vendor_id: g.vendor_id && Number(g.vendor_id) > 0 ? String(g.vendor_id) : '',
      employee_id: g.employee_id && Number(g.employee_id) > 0 ? String(g.employee_id) : '',
      employee_codfunc: '', // ao replicar, o employee_id vem do banco (id real)
      filter_ids: goalFilterIds,
      variable_ids: goalVarIds,
      goal_type: g.goal_type || 'monthly_revenue',
      goal_subtype: g.goal_subtype || '',
      goal_classification: g.goal_classification || (matchedPreset?.goal_classification || 'vendedor'),
      reference_year: g.reference_year || new Date().getFullYear(),
      reference_month: g.reference_month || new Date().getMonth() + 1,
      target_amount: formatInitialAmount(g.target_amount),
      target_quantity: isQuantitySubtype(g.goal_subtype) ? String(g.target_quantity || '') : '',
      unit_value: g.unit_value ? String(Number(g.unit_value)) : '',
      bonus_amount: g.bonus_amount ? formatInitialAmount(g.bonus_amount) : '',
      product_ids: g.product_ids || '',
      notes: g.notes ? `${g.notes} (Cópia)` : '(Cópia)',
      ref_daily_net_sales: formatInitialAmount(g.ref_daily_net_sales),
      ref_daily_orders_qty: g.ref_daily_orders_qty != null ? String(g.ref_daily_orders_qty).replace('.', ',') : '',
      ref_average_ticket: formatInitialAmount(g.ref_average_ticket),
      ref_items_per_attendance: g.ref_items_per_attendance != null ? String(g.ref_items_per_attendance).replace('.', ',') : '',
      ref_item_value_per_attendance: formatInitialAmount(g.ref_item_value_per_attendance),
      filter_configs: g.filter_configs ? { ...g.filter_configs } : {}
    });
    setShowModal(true);
  };

  const openNewModal = () => {
    resetForm();
    setSelectedPresetId('');
    setSuggestedPreset(null);
    setShowModal(true);
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (filteredItems) => {
    if (selectedIds.length === filteredItems.length && filteredItems.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredItems.map((g) => g.id));
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const id of selectedIds) {
        try {
          await faturamentoService.deleteGoal(id);
          successCount++;
        } catch (e) {
          console.error(`Erro ao excluir meta ${id}:`, e);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        setMessage({ type: 'success', text: `${successCount} metas excluídas com sucesso.` });
      } else {
        setMessage({ type: 'warning', text: `${successCount} metas excluídas, ${errorCount} falharam.` });
      }
      
      setShowBulkDeleteModal(false);
      setSelectedIds([]);
      setPage(1);
      loadGoals({ page: 1, per_page: perPage });
    } catch (e) {
      setMessage({ type: 'danger', text: 'Erro ao excluir lote de metas.' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const getGoalTypeLabel = (goalType, goalSubtype) => {
    const typeMap = {
      monthly_revenue: 'Mensal',
      quarterly_revenue: 'Trimestral',
      yearly_revenue: 'Anual',
    };
    const base = typeMap[goalType] || (goalType ? goalType.replace(/_/g, ' ') : '-');
    if (goalSubtype) {
      const subtypeMap = {
        clientes_positivados: 'Clientes Positivados',
        produtos_destaque: 'Produtos Destaque',
      };
      return `${base} (${subtypeMap[goalSubtype] || goalSubtype})`;
    }
    return base;
  };

  const GOAL_CLASSIFICATIONS = [
    { value: 'vendedor', label: 'Vendedor', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
    { value: 'bijou', label: 'Bijou', color: '#db2777', bg: '#fdf2f8', border: '#fbcfe8' },
    { value: 'operador_caixa', label: 'Operador de Caixa', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  ];

  const getGoalClassificationLabel = (classification) => {
    const found = GOAL_CLASSIFICATIONS.find(c => c.value === classification);
    return found ? found.label : (classification || 'Vendedor');
  };

  const getGoalClassificationBadge = (classification) => {
    const val = classification || 'vendedor';
    const item = GOAL_CLASSIFICATIONS.find(c => c.value === val) || GOAL_CLASSIFICATIONS[0];
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '12px',
        fontSize: '0.75rem', fontWeight: 600, backgroundColor: item.bg, color: item.color, border: `1px solid ${item.border}`,
        whiteSpace: 'nowrap'
      }}>
        {item.label}
      </span>
    );
  };

  const GOAL_SUBTYPES = [
    { value: '', label: 'Vendas (padrão)', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
    { value: 'clientes_positivados', label: 'Clientes Positivados', color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd' },
    { value: 'produtos_destaque', label: 'Produtos Destaque', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  ];

  const getGoalSubtypeLabel = (subtype) => {
    const found = GOAL_SUBTYPES.find(s => s.value === (subtype || ''));
    return found ? found.label : (subtype || 'Vendas (padrão)');
  };

  const getGoalSubtypeBadge = (subtype) => {
    const item = GOAL_SUBTYPES.find(s => s.value === (subtype || '')) || GOAL_SUBTYPES[0];
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '12px',
        fontSize: '0.72rem', fontWeight: 600, backgroundColor: item.bg, color: item.color, border: `1px solid ${item.border}`,
        whiteSpace: 'nowrap'
      }}>
        {item.label}
      </span>
    );
  };

  // Resolução sucessiva da filial da meta: 1. Grupo de Metas -> 2. Vendedor -> 3. Funcionário -> 4. Backend/Fallback
  const resolveGoalBranch = useCallback((g) => {
    // 1. Grupo de metas vinculado à meta (prioridade máxima do modelo/preset)
    if (g.preset_id && goalPresets && goalPresets.length > 0) {
      const p = goalPresets.find(pr => Number(pr.id) === Number(g.preset_id));
      if (p && p.filter_branch) {
        return {
          code: String(p.filter_branch).trim(),
          name: g.branch_name || (p.name ? p.name : `Filial ${p.filter_branch}`),
          source: 'preset'
        };
      }
    }
    // Dados resolvidos pelo backend
    if (g.branch_code) {
      return {
        code: String(g.branch_code).trim(),
        name: g.branch_name || `Filial ${g.branch_code}`,
        source: 'backend'
      };
    }
    // 2. Vendedor vinculado à meta
    if (g.vendor_id) {
      const vCode = vendorBranchMap[String(g.vendor_id)] || (g.vendor_codfilial ? String(g.vendor_codfilial) : null);
      if (vCode) {
        return {
          code: String(vCode).trim(),
          name: g.branch_name || `Filial ${vCode}`,
          source: 'vendor'
        };
      }
    }
    // 3. Funcionário vinculado à meta
    if (g.employee_id) {
      const eCode = employeeBranchMap[String(g.employee_id)] || (g.employee_codfilial ? String(g.employee_codfilial) : null);
      if (eCode) {
        return {
          code: String(eCode).trim(),
          name: g.branch_name || `Filial ${eCode}`,
          source: 'employee'
        };
      }
    }
    return null;
  }, [goalPresets, vendorBranchMap, employeeBranchMap]);

  const resolveGoalBranchBadge = (g) => {
    const branchInfo = resolveGoalBranch(g);
    if (!branchInfo || !branchInfo.code) {
      return <span className="text-muted small">-</span>;
    }
    const codes = String(branchInfo.code).split(',').map(c => c.trim()).filter(Boolean);
    return (
      <div className="d-flex flex-column align-items-start gap-1">
        <div className="d-flex flex-wrap gap-1 align-items-center">
          {codes.map((code) => (
            <span
              key={code}
              className="badge-modern"
              style={{
                backgroundColor: '#fef3c7',
                color: '#92400e',
                border: '1px solid #fde68a',
                fontWeight: 600,
                fontSize: '0.72rem',
                padding: '2px 7px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                whiteSpace: 'nowrap'
              }}
              title={`Filial ${code}`}
            >
              <FiMapPin size={11} />
              Filial {code}
            </span>
          ))}
        </div>
        {branchInfo.name && branchInfo.name !== `Filial ${branchInfo.code}` && (
          <span
            className="text-muted"
            style={{ fontSize: '0.72rem', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={branchInfo.name}
          >
            {branchInfo.name}
          </span>
        )}
      </div>
    );
  };

  const fetchCopyCandidates = async (sourceGoal, overrides = {}) => {
    if (!sourceGoal) return;
    setCopyRefModal(prev => ({ ...prev, loading: true }));
    try {
      const q = {
        goal_type: sourceGoal.goal_type,
        goal_classification: sourceGoal.goal_classification || 'vendedor',
        per_page: 1000,
        sort_by: 'vendor',
        sort_order: 'asc'
      };
      if (sourceGoal.goal_subtype) {
        q.goal_subtype = sourceGoal.goal_subtype;
      }
      if (overrides.filterYear) q.year = overrides.filterYear;
      if (overrides.filterMonth) q.month = overrides.filterMonth;
      if (overrides.filterBranch) q.filter_branch = overrides.filterBranch;
      if (overrides.searchTerm && overrides.searchTerm.trim()) q.search = overrides.searchTerm.trim();

      const res = await faturamentoService.listGoals(q);
      const rawList = res?.data?.goals || [];

      // Filtragem estrita da mesma natureza e excluindo a meta de origem
      const filtered = rawList.filter(c =>
        Number(c.id) !== Number(sourceGoal.id) &&
        c.goal_type === sourceGoal.goal_type &&
        (c.goal_subtype || '') === (sourceGoal.goal_subtype || '') &&
        (c.goal_classification || 'vendedor') === (sourceGoal.goal_classification || 'vendedor')
      );

      setCopyRefModal(prev => ({
        ...prev,
        candidates: filtered,
        loading: false
      }));
    } catch (err) {
      console.error('Erro ao buscar metas compatíveis para cópia:', err);
      setCopyRefModal(prev => ({ ...prev, loading: false }));
    }
  };

  const openCopyReferencesModal = (goal) => {
    if (!goal) return;
    const initialYear = filterYear || (goal.reference_year ? String(goal.reference_year) : String(new Date().getFullYear()));
    const initialMonth = filterMonth || (goal.reference_month ? String(goal.reference_month) : '');
    const initialBranch = filterBranchSearch || (goal.branch_code ? String(goal.branch_code) : '');

    setCopyRefModal({
      open: true,
      sourceGoal: goal,
      targetGoalIds: [],
      loading: true,
      applying: false,
      candidates: [],
      filterYear: initialYear,
      filterMonth: initialMonth,
      filterBranch: initialBranch,
      searchTerm: '',
      options: {
        copy_bi_references: true,
        copy_filters: true,
        copy_filter_configs: true,
        copy_variables: true,
        copy_bonus_params: true,
        copy_target_value: false,
        copy_notes: false
      }
    });

    fetchCopyCandidates(goal, {
      filterYear: initialYear,
      filterMonth: initialMonth,
      filterBranch: initialBranch,
      searchTerm: ''
    });
  };

  const handleCopyModalFilterChange = (field, val) => {
    setCopyRefModal(prev => {
      const updatedOverrides = {
        filterYear: field === 'filterYear' ? val : prev.filterYear,
        filterMonth: field === 'filterMonth' ? val : prev.filterMonth,
        filterBranch: field === 'filterBranch' ? val : prev.filterBranch,
        searchTerm: field === 'searchTerm' ? val : prev.searchTerm
      };
      if (prev.sourceGoal) {
        fetchCopyCandidates(prev.sourceGoal, updatedOverrides);
      }
      return { ...prev, [field]: val };
    });
  };

  const handleCopyModalSearchChange = (val) => {
    setCopyRefModal(prev => {
      if (copySearchTimeoutRef.current) clearTimeout(copySearchTimeoutRef.current);
      const currentGoal = prev.sourceGoal;
      const currentYear = prev.filterYear;
      const currentMonth = prev.filterMonth;
      const currentBranch = prev.filterBranch;
      copySearchTimeoutRef.current = setTimeout(() => {
        if (currentGoal) {
          fetchCopyCandidates(currentGoal, {
            filterYear: currentYear,
            filterMonth: currentMonth,
            filterBranch: currentBranch,
            searchTerm: val
          });
        }
      }, 350);
      return { ...prev, searchTerm: val };
    });
  };

  const toggleCopyTargetGoal = (id) => {
    setCopyRefModal(prev => {
      const exists = prev.targetGoalIds.includes(id);
      return {
        ...prev,
        targetGoalIds: exists
          ? prev.targetGoalIds.filter(x => x !== id)
          : [...prev.targetGoalIds, id]
      };
    });
  };

  const toggleSelectAllCopyTargets = () => {
    setCopyRefModal(prev => {
      const allCandidateIds = prev.candidates.map(c => c.id);
      const allSelected = allCandidateIds.length > 0 && allCandidateIds.every(id => prev.targetGoalIds.includes(id));
      return {
        ...prev,
        targetGoalIds: allSelected ? [] : allCandidateIds
      };
    });
  };

  const toggleCopyOption = (key) => {
    setCopyRefModal(prev => ({
      ...prev,
      options: {
        ...prev.options,
        [key]: !prev.options[key]
      }
    }));
  };

  const handleApplyCopyReferences = async () => {
    if (!copyRefModal.sourceGoal) return;
    if (copyRefModal.targetGoalIds.length === 0) {
      alert('Selecione pelo menos uma meta de destino para aplicar a cópia.');
      return;
    }
    const hasAnyOption = Object.values(copyRefModal.options).some(Boolean);
    if (!hasAnyOption) {
      alert('Selecione pelo menos um item para copiar.');
      return;
    }

    try {
      setCopyRefModal(prev => ({ ...prev, applying: true }));
      const payload = {
        target_goal_ids: copyRefModal.targetGoalIds,
        ...copyRefModal.options
      };
      const res = await faturamentoService.copyGoalReferences(copyRefModal.sourceGoal.id, payload);
      setMessage({
        type: 'success',
        text: res?.message || `${copyRefModal.targetGoalIds.length} metas atualizadas com sucesso!`
      });
      setCopyRefModal(prev => ({ ...prev, open: false, applying: false }));
      loadGoals();
    } catch (err) {
      console.error('Erro ao copiar referências:', err);
      const errMsg = err.response?.data?.error || 'Erro ao aplicar cópia de referências';
      alert(errMsg);
      setCopyRefModal(prev => ({ ...prev, applying: false }));
    }
  };

  const sortedGoals = useMemo(() => {
    const list = [...goals];
    if (!sortField) return list;

    list.sort((a, b) => {
      let res = 0;
      switch (sortField) {
        case 'id':
          res = Number(a.id || 0) - Number(b.id || 0);
          break;
        case 'vendor':
        case 'vendor_name': {
          const nameA = (a.vendor_name || a.employee_name || '').toLowerCase();
          const nameB = (b.vendor_name || b.employee_name || '').toLowerCase();
          res = nameA.localeCompare(nameB, 'pt-BR');
          break;
        }
        case 'goal_type': {
          const typeA = getGoalTypeLabel(a.goal_type, a.goal_subtype).toLowerCase();
          const typeB = getGoalTypeLabel(b.goal_type, b.goal_subtype).toLowerCase();
          res = typeA.localeCompare(typeB, 'pt-BR');
          break;
        }
        case 'goal_classification':
        case 'classification': {
          const classA = (a.goal_classification || 'vendedor').toLowerCase();
          const classB = (b.goal_classification || 'vendedor').toLowerCase();
          res = classA.localeCompare(classB, 'pt-BR');
          break;
        }
        case 'period': {
          const perA = (Number(a.reference_year || 0) * 100) + Number(a.reference_month || 0);
          const perB = (Number(b.reference_year || 0) * 100) + Number(b.reference_month || 0);
          res = perA - perB;
          break;
        }
        case 'filters': {
          const countA = (a.filter_ids || []).length;
          const countB = (b.filter_ids || []).length;
          res = countA - countB;
          break;
        }
        case 'variables': {
          const countA = (a.variable_ids || []).length;
          const countB = (b.variable_ids || []).length;
          res = countA - countB;
          break;
        }
        case 'branch':
        case 'branch_code': {
          const brA = resolveGoalBranch(a)?.code || '';
          const brB = resolveGoalBranch(b)?.code || '';
          res = brA.localeCompare(brB, 'pt-BR', { numeric: true });
          break;
        }
        case 'value':
        case 'target_amount': {
          const valA = Number(a.target_amount || a.target_quantity || 0);
          const valB = Number(b.target_amount || b.target_quantity || 0);
          res = valA - valB;
          break;
        }
        default:
          res = 0;
      }
      return sortOrder === 'desc' ? -res : res;
    });

    return list;
  }, [goals, sortField, sortOrder, resolveGoalBranch]);

  const executeSave = async (payload) => {
    setSaving(true);
    setMessage(null);
    try {
      if (form.id) {
        await faturamentoService.updateGoal(form.id, payload);
        setMessage({ type: 'success', text: 'Meta atualizada.' });
      } else {
        await faturamentoService.createGoal(payload);
        setMessage({ type: 'success', text: 'Meta criada.' });
      }
      resetForm();
      setShowModal(false);
      setPage(1);
      await loadGoals({ page: 1, per_page: perPage });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erro ao cadastrar meta.';
      if (showModal) {
        setErrorModalTitle('Erro ao Salvar');
        setErrorMessage(errorMsg);
        setShowErrorModal(true);
      } else {
        setMessage({ type: 'danger', text: errorMsg });
      }
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!selectedPresetId && (!form.filter_ids || form.filter_ids.length === 0) && (!form.variable_ids || form.variable_ids.length === 0)) {
      setErrorModalTitle('Grupo de Metas Obrigatório');
      setErrorMessage('Por favor, selecione um Grupo de Metas obrigatório para cadastrar a meta. Os filtros e variáveis são definidos pelo grupo.');
      setShowErrorModal(true);
      return;
    }

    if (!form.vendor_id && !form.employee_id) {
      setErrorModalTitle('Colaborador Obrigatório');
      setErrorMessage('Por favor, selecione um Vendedor ou Funcionário para esta meta.');
      setShowErrorModal(true);
      return;
    }

    const isQty = isQuantitySubtype(form.goal_subtype);
    const hasEmployee = Boolean(form.employee_id && parseInt(form.employee_id, 10) > 0);
    const payload = {
      vendor_id: hasEmployee ? null : (form.vendor_id && parseInt(form.vendor_id, 10) > 0 ? parseInt(form.vendor_id, 10) : null),
      // Se employee_codfunc estiver preenchido, envia como codfunc para que o backend resolva pelo código Winthor
      employee_id: hasEmployee ? parseInt(form.employee_id, 10) : null,
      codfunc: hasEmployee && form.employee_codfunc && parseInt(form.employee_codfunc, 10) > 0 ? parseInt(form.employee_codfunc, 10) : undefined,
      preset_id: selectedPresetId && parseInt(selectedPresetId, 10) > 0 ? parseInt(selectedPresetId, 10) : null,
      filter_ids: form.filter_ids,
      variable_ids: form.variable_ids,
      goal_type: form.goal_type,
      goal_subtype: form.goal_subtype || null,
      goal_classification: form.goal_classification || 'vendedor',
      reference_year: form.reference_year ? parseInt(form.reference_year, 10) : null,
      target_amount: isQty
        ? (form.target_amount ? parseFloat(parseCurrencyBRL(String(form.target_amount))) : null)
        : parseFloat(parseCurrencyBRL(String(form.target_amount))),
      target_quantity: isQty ? (form.target_quantity ? parseInt(form.target_quantity, 10) : null) : undefined,
      unit_value: isQty ? (form.unit_value !== '' ? parseFloat(form.unit_value) : null) : undefined,
      bonus_amount: isQty ? (form.bonus_amount ? parseFloat(parseCurrencyBRL(String(form.bonus_amount))) : null) : undefined,
      product_ids: form.product_ids || null,
      notes: form.notes || null,
      ref_daily_net_sales: form.ref_daily_net_sales ? parseFloat(parseCurrencyBRL(String(form.ref_daily_net_sales))) : null,
      ref_daily_orders_qty: form.ref_daily_orders_qty !== '' && form.ref_daily_orders_qty != null ? parseFloat(String(form.ref_daily_orders_qty).replace(',', '.')) : null,
      ref_average_ticket: form.ref_average_ticket ? parseFloat(parseCurrencyBRL(String(form.ref_average_ticket))) : null,
      ref_items_per_attendance: form.ref_items_per_attendance !== '' && form.ref_items_per_attendance != null ? parseFloat(String(form.ref_items_per_attendance).replace(',', '.')) : null,
      ref_item_value_per_attendance: form.ref_item_value_per_attendance ? parseFloat(parseCurrencyBRL(String(form.ref_item_value_per_attendance))) : null,
      filter_configs: form.filter_configs || {}
    };

    if (form.goal_type === 'monthly_revenue' || form.goal_type === 'quarterly_revenue') {
      payload.reference_month = form.reference_month ? parseInt(form.reference_month, 10) : null;
    } else {
      payload.reference_month = null;
    }

    await executeSave(payload);
  };

  const remove = async (id) => {
    const goal = goals.find(g => g.id === id);
    setGoalToDelete(goal);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!goalToDelete) return;
    setDeleting(true);
    try {
      await faturamentoService.deleteGoal(goalToDelete.id);
      setMessage({ type: 'success', text: `Meta removida com sucesso!` });
      setShowDeleteModal(false);
      setGoalToDelete(null);
      setPage(1);
      loadGoals({ page: 1, per_page: perPage });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erro ao remover meta.';
      setErrorModalTitle('Erro ao Remover');
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setDeleting(false);
    }
  };

  const periodLabel = (g) => {
    if (g.goal_type === 'yearly_revenue') return `${g.reference_year}`;
    if (g.goal_type === 'quarterly_revenue') return `${g.reference_year} - T${g.reference_month}`;
    const m = MESES.find((x) => x.v === g.reference_month);
    return `${m?.n || g.reference_month}/${g.reference_year}`;
  };

  const resolveFilterNames = (g) => {
    if (g.filter_names) return g.filter_names;
    const ids = Array.isArray(g.filter_ids) ? g.filter_ids : [];
    if (ids.length === 0) return 'Filtro não vinculado';
    const names = ids.map((id) => filters.find((f) => String(f.id) === String(id))?.name).filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'Filtro não vinculado';
  };

  const resolveVariableNames = (g) => {
    let ids = parseVariableIds(g.variable_ids);
    if (ids.length === 0) return 'Nenhuma variável';
    const names = ids.map((id) => variables.find((v) => String(v.id) === String(id))?.name).filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'Nenhuma variável';
  };

  const toggleExpand = (goalId) => {
    setExpandedGoalId(expandedGoalId === goalId ? null : goalId);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (!form.id) resetForm();
  };

  // Helpers para edicao inline de validacao de filtros e variaveis (unitária e múltipla)
  const updateFilterField = (filterId, field, val) => {
    const fId = Number(filterId);
    setFiltersEditData(prev => ({
      ...prev,
      [fId]: {
        ...(prev[fId] || {}),
        [field]: val
      }
    }));
    if (editingFilterId === fId) {
      setFilterEditData(prev => ({ ...prev, [field]: val }));
    }
  };

  const startEditFilter = (f) => {
    const fId = Number(f.filter_id || f.id);
    const initialData = {
      start_date: (f.start_date && f.start_date !== '0000-00-00') ? f.start_date : '',
      end_date: (f.end_date && f.end_date !== '0000-00-00') ? f.end_date : '',
      commission_percent: f.commission_percent,
      commission_fixed_per_unit: f.commission_fixed_per_unit,
      commission_min_products_qty: f.commission_min_products_qty,
      commission_min_sold_value: f.commission_min_sold_value,
      commission_use_goal_value: f.commission_use_goal_value,
      show_commission_percentage: f.show_commission_percentage,
      bonus_min_products_qty: f.bonus_min_products_qty,
      bonus_min_sold_value: f.bonus_min_sold_value,
      bonus_amount: f.bonus_amount,
      include_in_goal_total: f.include_in_goal_total,
      include_commission_in_goal_total: f.include_commission_in_goal_total,
    };
    setEditingFilterIds(prev => prev.includes(fId) ? prev : [...prev, fId]);
    setFiltersEditData(prev => ({
      ...prev,
      [fId]: initialData
    }));
    setEditingFilterId(fId);
    setFilterEditData(initialData);
  };

  const cancelEditFilter = (fId) => {
    const numId = Number(fId);
    setEditingFilterIds(prev => prev.filter(id => id !== numId));
    setFiltersEditData(prev => {
      const next = { ...prev };
      delete next[numId];
      return next;
    });
    if (editingFilterId === numId) {
      setEditingFilterId(null);
      setFilterEditData({});
    }
  };

  const startEditAllFilters = (filterList) => {
    const allIds = [];
    const allData = {};
    (filterList || []).forEach(f => {
      const fId = Number(f.filter_id || f.id);
      allIds.push(fId);
      allData[fId] = {
        start_date: (f.start_date && f.start_date !== '0000-00-00') ? f.start_date : '',
        end_date: (f.end_date && f.end_date !== '0000-00-00') ? f.end_date : '',
        commission_percent: f.commission_percent,
        commission_fixed_per_unit: f.commission_fixed_per_unit,
        commission_min_products_qty: f.commission_min_products_qty,
        commission_min_sold_value: f.commission_min_sold_value,
        commission_use_goal_value: f.commission_use_goal_value,
        show_commission_percentage: f.show_commission_percentage,
        bonus_min_products_qty: f.bonus_min_products_qty,
        bonus_min_sold_value: f.bonus_min_sold_value,
        bonus_amount: f.bonus_amount,
        include_in_goal_total: f.include_in_goal_total,
        include_commission_in_goal_total: f.include_commission_in_goal_total,
      };
    });
    setEditingFilterIds(prev => Array.from(new Set([...prev, ...allIds])));
    setFiltersEditData(prev => ({ ...prev, ...allData }));
  };

  const cancelEditAllFilters = (filterList) => {
    const idsToRemove = new Set((filterList || []).map(f => Number(f.filter_id || f.id)));
    setEditingFilterIds(prev => prev.filter(id => !idsToRemove.has(id)));
    setFiltersEditData(prev => {
      const next = { ...prev };
      idsToRemove.forEach(id => delete next[id]);
      return next;
    });
    if (editingFilterId && idsToRemove.has(editingFilterId)) {
      setEditingFilterId(null);
      setFilterEditData({});
    }
  };

  const requestSaveFilterValidation = (f) => {
    const fId = Number(f.filter_id || f.id);
    const editData = filtersEditData[fId] || filtersEditData[f.id] || filterEditData;
    const saveData = { ...editData };
    if (!saveData.start_date) saveData.start_date = null;
    if (!saveData.end_date) saveData.end_date = null;
    setFilterConfirmModal({
      open: true,
      filter: { ...f, id: fId, filter_id: fId },
      saveData: saveData,
      isBatch: false,
      batchItems: []
    });
  };

  const requestSaveAllFiltersValidation = (filterList) => {
    const targetFilters = (filterList || []).filter(f => editingFilterIds.includes(Number(f.filter_id || f.id)));
    if (targetFilters.length === 0) return;

    if (targetFilters.length === 1) {
      requestSaveFilterValidation(targetFilters[0]);
      return;
    }

    const batchItems = targetFilters.map(f => {
      const fId = Number(f.filter_id || f.id);
      const editData = filtersEditData[fId] || filtersEditData[f.id] || {};
      const saveData = { ...editData };
      if (!saveData.start_date) saveData.start_date = null;
      if (!saveData.end_date) saveData.end_date = null;
      return { filter: { ...f, id: fId, filter_id: fId }, saveData };
    });

    setFilterConfirmModal({
      open: true,
      filter: null,
      saveData: null,
      isBatch: true,
      batchItems
    });
  };

  const handleConfirmSaveFilterOnlyGoal = async () => {
    if (filterConfirmModal.isBatch && Array.isArray(filterConfirmModal.batchItems)) {
      setSavingFilterValid(true);
      try {
        const newConfigs = {};
        const promises = filterConfirmModal.batchItems.map(async ({ filter: f, saveData }) => {
          const realFilterId = Number(f.filter_id || f.id);
          const customConfig = {
            ...saveData,
            goal_id: form.id || null,
            filter_id: realFilterId,
            id: realFilterId,
            is_custom: 1
          };
          newConfigs[realFilterId] = customConfig;
          if (form.id) {
            await faturamentoService.saveGoalFilterConfig(form.id, realFilterId, {
              ...saveData,
              update_base: false
            });
          }
        });

        await Promise.all(promises);

        setForm(prev => ({
          ...prev,
          filter_configs: {
            ...(prev.filter_configs || {}),
            ...newConfigs
          }
        }));

        cancelEditAllFilters(filterConfirmModal.batchItems.map(b => b.filter));
        setFilterConfirmModal({ open: false, filter: null, saveData: null, isBatch: false, batchItems: [] });
        setMessage({ type: 'success', text: `Configurações de ${filterConfirmModal.batchItems.length} filtros salvas exclusivamente para esta meta!` });
      } catch (e) {
        setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao salvar configurações dos filtros para a meta' });
      } finally {
        setSavingFilterValid(false);
      }
      return;
    }

    if (!filterConfirmModal.filter || !filterConfirmModal.saveData) return;
    const f = filterConfirmModal.filter;
    const realFilterId = Number(f.filter_id || f.id);
    const saveData = filterConfirmModal.saveData;
    setSavingFilterValid(true);
    try {
      const customConfig = {
        ...saveData,
        goal_id: form.id || null,
        filter_id: realFilterId,
        id: realFilterId,
        is_custom: 1
      };

      setForm(prev => ({
        ...prev,
        filter_configs: {
          ...(prev.filter_configs || {}),
          [realFilterId]: customConfig
        }
      }));

      if (form.id) {
        await faturamentoService.saveGoalFilterConfig(form.id, realFilterId, {
          ...saveData,
          update_base: false
        });
      }

      cancelEditFilter(realFilterId);
      setFilterConfirmModal({ open: false, filter: null, saveData: null, isBatch: false, batchItems: [] });
      setMessage({ type: 'success', text: `Configurações salvas exclusivamente para esta meta!` });
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao salvar configuração do filtro para a meta' });
    } finally {
      setSavingFilterValid(false);
    }
  };

  const handleConfirmSaveFilterAndUpdateBase = async () => {
    if (filterConfirmModal.isBatch && Array.isArray(filterConfirmModal.batchItems)) {
      setSavingFilterValid(true);
      try {
        const newConfigs = {};
        const updatedFiltersMap = {};
        const promises = filterConfirmModal.batchItems.map(async ({ filter: f, saveData }) => {
          const realFilterId = Number(f.filter_id || f.id);
          const customConfig = {
            ...saveData,
            goal_id: form.id || null,
            filter_id: realFilterId,
            id: realFilterId,
            is_custom: 1
          };
          newConfigs[realFilterId] = customConfig;
          updatedFiltersMap[realFilterId] = saveData;
          if (form.id) {
            await faturamentoService.saveGoalFilterConfig(form.id, realFilterId, {
              ...saveData,
              update_base: true
            });
          } else {
            await faturamentoService.updateSavedFilter(realFilterId, saveData);
          }
        });

        await Promise.all(promises);

        setForm(prev => ({
          ...prev,
          filter_configs: {
            ...(prev.filter_configs || {}),
            ...newConfigs
          }
        }));

        setFilters(prev => prev.map(fv => updatedFiltersMap[Number(fv.id)] ? { ...fv, ...updatedFiltersMap[Number(fv.id)] } : fv));
        cancelEditAllFilters(filterConfirmModal.batchItems.map(b => b.filter));
        setFilterConfirmModal({ open: false, filter: null, saveData: null, isBatch: false, batchItems: [] });
        setMessage({ type: 'success', text: `Configurações de ${filterConfirmModal.batchItems.length} filtros salvas para a meta e atualizadas no cadastro base!` });
      } catch (e) {
        setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao atualizar filtros base e meta' });
      } finally {
        setSavingFilterValid(false);
      }
      return;
    }

    if (!filterConfirmModal.filter || !filterConfirmModal.saveData) return;
    const f = filterConfirmModal.filter;
    const realFilterId = Number(f.filter_id || f.id);
    const saveData = filterConfirmModal.saveData;
    setSavingFilterValid(true);
    try {
      const customConfig = {
        ...saveData,
        goal_id: form.id || null,
        filter_id: realFilterId,
        id: realFilterId,
        is_custom: 1
      };

      setForm(prev => ({
        ...prev,
        filter_configs: {
          ...(prev.filter_configs || {}),
          [realFilterId]: customConfig
        }
      }));

      if (form.id) {
        await faturamentoService.saveGoalFilterConfig(form.id, realFilterId, {
          ...saveData,
          update_base: true
        });
      } else {
        await faturamentoService.updateSavedFilter(realFilterId, saveData);
      }

      setFilters(prev => prev.map(fv => Number(fv.id) === realFilterId ? { ...fv, ...saveData } : fv));
      cancelEditFilter(realFilterId);
      setFilterConfirmModal({ open: false, filter: null, saveData: null, isBatch: false, batchItems: [] });
      setMessage({ type: 'success', text: `Configurações salvas para a meta e atualizadas no cadastro base do filtro!` });
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao atualizar filtro base e meta' });
    } finally {
      setSavingFilterValid(false);
    }
  };

  const handleRestoreFilterDefault = async (filterId) => {
    const realFilterId = Number(filterId);
    try {
      if (form.id) {
        await faturamentoService.deleteGoalFilterConfig(form.id, realFilterId);
      }
      setForm(prev => {
        const nextConfigs = { ...(prev.filter_configs || {}) };
        delete nextConfigs[realFilterId];
        return { ...prev, filter_configs: nextConfigs };
      });
      setMessage({ type: 'info', text: 'Configuração exclusiva removida. Valores restaurados do filtro base.' });
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao restaurar padrão do filtro' });
    }
  };

  const updateVariableField = (vId, field, val) => {
    const numId = Number(vId);
    setVariablesEditData(prev => ({
      ...prev,
      [numId]: {
        ...(prev[numId] || {}),
        [field]: val
      }
    }));
    if (editingVariableId === numId) {
      setVariableEditData(prev => ({ ...prev, [field]: val }));
    }
  };

  const startEditVariable = (v) => {
    const vId = Number(v.id);
    const initialData = {
      formula: v.formula ?? '',
      commission_percent: v.commission_percent ?? '',
      commission_min_sold_value: v.commission_min_sold_value ?? '',
      commission_use_goal_value: v.commission_use_goal_value ?? 0,
      use_sold_qty_for_bonus: v.use_sold_qty_for_bonus ?? 0,
      bonus_validation_qty: v.bonus_validation_qty ?? '',
      variable_bonus_amount: v.variable_bonus_amount ?? '',
      has_accrual_rule: v.has_accrual_rule ?? 0,
      accrual_rules: (v.accrual_rules || []).map(r => ({ ...r })),
    };
    setEditingVariableIds(prev => prev.includes(vId) ? prev : [...prev, vId]);
    setVariablesEditData(prev => ({
      ...prev,
      [vId]: initialData
    }));
    setEditingVariableId(vId);
    setVariableEditData(initialData);
  };

  const cancelEditVariable = (vId) => {
    const numId = Number(vId);
    setEditingVariableIds(prev => prev.filter(id => id !== numId));
    setVariablesEditData(prev => {
      const next = { ...prev };
      delete next[numId];
      return next;
    });
    if (editingVariableId === numId) {
      setEditingVariableId(null);
      setVariableEditData({});
    }
  };

  const startEditAllVariables = () => {
    const linkedVars = form.variable_ids
      .map(vid => variables.find(vv => Number(vv.id) === Number(vid)))
      .filter(Boolean);
    const allIds = [];
    const allData = {};
    linkedVars.forEach(v => {
      const vId = Number(v.id);
      allIds.push(vId);
      allData[vId] = {
        formula: v.formula ?? '',
        commission_percent: v.commission_percent ?? '',
        commission_min_sold_value: v.commission_min_sold_value ?? '',
        commission_use_goal_value: v.commission_use_goal_value ?? 0,
        use_sold_qty_for_bonus: v.use_sold_qty_for_bonus ?? 0,
        bonus_validation_qty: v.bonus_validation_qty ?? '',
        variable_bonus_amount: v.variable_bonus_amount ?? '',
        has_accrual_rule: v.has_accrual_rule ?? 0,
        accrual_rules: (v.accrual_rules || []).map(r => ({ ...r })),
      };
    });
    setEditingVariableIds(allIds);
    setVariablesEditData(prev => ({ ...prev, ...allData }));
  };

  const cancelEditAllVariables = () => {
    setEditingVariableIds([]);
    setVariablesEditData({});
    setEditingVariableId(null);
    setVariableEditData({});
  };

  const saveVariableValidation = async (v) => {
    const vId = Number(v.id);
    const editData = variablesEditData[vId] || variableEditData;
    setSavingVariable(true);
    try {
      const res = await faturamentoService.updateVariable(vId, editData);
      const updated = res?.data?.variable || { ...v, ...editData };
      setVariables(prev => prev.map(vv => Number(vv.id) === vId ? updated : vv));
      cancelEditVariable(vId);
      setMessage({ type: 'success', text: `Variável "${v.name}" atualizada com sucesso!` });
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao atualizar variável' });
    } finally {
      setSavingVariable(false);
    }
  };

  const saveAllVariablesValidation = async () => {
    if (editingVariableIds.length === 0) return;
    setSavingVariable(true);
    try {
      const promises = editingVariableIds.map(async (vId) => {
        const editData = variablesEditData[vId];
        if (!editData) return null;
        const res = await faturamentoService.updateVariable(vId, editData);
        const updated = res?.data?.variable || { id: vId, ...editData };
        return { id: vId, data: updated };
      });

      const results = await Promise.all(promises);
      const updatedMap = {};
      results.filter(Boolean).forEach(r => {
        updatedMap[Number(r.id)] = r.data;
      });

      setVariables(prev => prev.map(vv => updatedMap[Number(vv.id)] ? { ...vv, ...updatedMap[Number(vv.id)] } : vv));
      cancelEditAllVariables();
      setMessage({ type: 'success', text: `${results.filter(Boolean).length} variável(is) vinculada(s) salva(s) com sucesso!` });
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao atualizar variáveis em lote' });
    } finally {
      setSavingVariable(false);
    }
  };

  const startEditAccrualRules = (v) => {
    setEditingAccrualVarId(Number(v.id));
    const currentRules = (v.accrual_rules || []).map(r => ({
      ...r,
      threshold: r.threshold != null ? String(r.threshold) : '',
      threshold_quantity: r.threshold_quantity != null ? String(r.threshold_quantity) : '',
      accrual_percent: r.accrual_percent != null ? String(r.accrual_percent) : '',
      accrual_amount: r.accrual_amount != null ? String(r.accrual_amount) : '',
      use_goal_value: Number(r.use_goal_value) === 1 ? 1 : 0,
      filter_start_date: (r.filter_start_date && r.filter_start_date !== '0000-00-00') ? r.filter_start_date.slice(0, 10) : '',
      filter_end_date: (r.filter_end_date && r.filter_end_date !== '0000-00-00') ? r.filter_end_date.slice(0, 10) : '',
    }));
    if (currentRules.length === 0) {
      currentRules.push({
        reference_type: 'filter',
        reference_id: '',
        threshold: '',
        threshold_quantity: '',
        accrual_percent: '',
        accrual_amount: '',
        use_goal_value: 0,
        filter_start_date: '',
        filter_end_date: ''
      });
    }
    setAccrualEditRules(currentRules);
    if (reports.length === 0) {
      loadReports();
    }
  };

  const cancelEditAccrualRules = () => {
    setEditingAccrualVarId(null);
    setAccrualEditRules([]);
  };

  const addAccrualRuleRow = () => {
    setAccrualEditRules(prev => [
      ...prev,
      {
        reference_type: 'filter',
        reference_id: '',
        threshold: '',
        threshold_quantity: '',
        accrual_percent: '',
        accrual_amount: '',
        use_goal_value: 0,
        filter_start_date: '',
        filter_end_date: ''
      }
    ]);
  };

  const removeAccrualRuleRow = (idx) => {
    setAccrualEditRules(prev => prev.filter((_, i) => i !== idx));
  };

  const updateAccrualRuleField = (idx, field, value) => {
    setAccrualEditRules(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'reference_type') {
        next[idx].reference_id = '';
      }
      return next;
    });
  };

  const saveAccrualRules = async (v) => {
    setSavingAccrualRules(true);
    try {
      const validRules = accrualEditRules
        .filter(r => r.reference_type && r.reference_id)
        .map(r => ({
          reference_type: r.reference_type,
          reference_id: Number(r.reference_id),
          threshold: r.use_goal_value ? 0 : (parseFloat(r.threshold) || 0),
          threshold_quantity: r.threshold_quantity !== '' && r.threshold_quantity != null ? parseInt(r.threshold_quantity, 10) : null,
          accrual_percent: parseFloat(r.accrual_percent) || 0,
          accrual_amount: parseFloat(r.accrual_amount) || 0,
          use_goal_value: r.use_goal_value ? 1 : 0,
          filter_start_date: r.filter_start_date || null,
          filter_end_date: r.filter_end_date || null,
        }));

      const payload = {
        has_accrual_rule: validRules.length > 0 ? 1 : 0,
        accrual_rules: validRules,
      };

      const res = await faturamentoService.updateVariable(v.id, payload);
      const updatedVar = res?.data?.variable;
      if (updatedVar) {
        setVariables(prev => prev.map(vv => Number(vv.id) === Number(v.id) ? updatedVar : vv));
      } else {
        setVariables(prev => prev.map(vv => Number(vv.id) === Number(v.id) ? {
          ...vv,
          has_accrual_rule: validRules.length > 0 ? 1 : 0,
          accrual_rules: validRules
        } : vv));
      }

      setEditingAccrualVarId(null);
      setAccrualEditRules([]);
      setMessage({ type: 'success', text: `Regras de acréscimo da variável "${v.name}" salvas com sucesso!` });
    } catch (e) {
      console.error('Erro ao salvar regras de acréscimo:', e);
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao salvar regras de acréscimo' });
    } finally {
      setSavingAccrualRules(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Metas</h2>
          <p className="text-muted mb-0">Mesmo modelo do dashboard de vendas.</p>
        </div>
        <button type="button" className="btn-modern btn-modern-light" onClick={() => loadGoals({ page, per_page: perPage })} disabled={loading}>
          <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} />
        </button>
      </div>

      {message && <div className={`alert alert-${message.type} py-2 mb-4`} role="alert">{message.text}</div>}

      {canManage && (
        <div className="mb-4">
          <button type="button" className="btn-modern btn-modern-primary" onClick={openNewModal}>
            <FiPlus size={16} className="me-2" />Nova Meta
          </button>
        </div>
      )}

      <div className="settings-container">
        <div className="card-modern shadow-sm-modern" style={{ overflow: 'visible' }}>
          <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ position: 'relative', zIndex: 100 }}>
            <div>
              <h5 className="mb-0 fw-bold text-dark">Metas cadastradas</h5>
            </div>
            
            <div className="d-flex align-items-center gap-3 flex-wrap" style={{ position: 'relative', zIndex: 100 }}>
              <div style={{ position: 'relative', width: '250px' }}>
                <FiSearch
                  size={16}
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', zIndex: 1 }}
                />
                <input
                  type="text"
                  className="form-control-modern"
                  placeholder="Buscar vendedor, funcionário, filial ou tipo..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  style={{ paddingLeft: '38px', height: '40px' }}
                />
              </div>
              
              <select
                className="form-control-modern"
                value={filterYear}
                onChange={(e) => {
                  setFilterYear(e.target.value);
                  setPage(1);
                }}
                style={{ width: '120px', height: '40px' }}
                title="Filtrar por ano"
              >
                <option value="">Todos os anos</option>
                {(() => {
                  const curY = new Date().getFullYear();
                  const yearSet = new Set([curY + 1, curY, curY - 1, curY - 2, curY - 3, ...goals.map(g => Number(g.reference_year)).filter(Boolean)]);
                  return Array.from(yearSet).sort((a, b) => b - a).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ));
                })()}
              </select>
              
              <select
                className="form-control-modern"
                value={filterMonth}
                onChange={(e) => {
                  setFilterMonth(e.target.value);
                  setPage(1);
                }}
                style={{ width: '140px', height: '40px' }}
                title="Filtrar por mês"
              >
                <option value="">Todos os meses</option>
                {MESES.map(m => (
                  <option key={m.v} value={m.v}>{m.n}</option>
                ))}
              </select>

              <div style={{ width: '220px', position: 'relative', zIndex: 110 }}>
                <MultiSelectSearch
                  label=""
                  placeholder="Filtrar filial..."
                  value={filterBranchSearch}
                  onChange={(val) => {
                    setFilterBranchSearch(val);
                    setPage(1);
                  }}
                  fetchData={filterDataService.getFiliais}
                  badgeColor="#10b981"
                  showCodePrefix
                />
              </div>

              <select
                className="form-control-modern"
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(1);
                }}
                style={{ width: '200px', height: '40px' }}
                title="Filtrar por tipo"
              >
                <option value="">Todos os tipos</option>
                <option value="monthly_revenue">Mensal</option>
                <option value="quarterly_revenue">Trimestral</option>
                <option value="yearly_revenue">Anual</option>
                <option value="clientes_positivados">Clientes Positivados</option>
                <option value="produtos_destaque">Produtos Destaque</option>
              </select>

              <select
                className="form-control-modern"
                value={filterClassification}
                onChange={(e) => {
                  setFilterClassification(e.target.value);
                  setPage(1);
                }}
                style={{ width: '180px', height: '40px' }}
                title="Filtrar por classificação"
              >
                <option value="">Todas as classificações</option>
                <option value="vendedor">Vendedor</option>
                <option value="bijou">Bijou</option>
                <option value="operador_caixa">Operador de Caixa</option>
              </select>

              {(searchTerm || filterYear || filterMonth || filterBranchSearch || filterType || filterClassification) && (
                <button
                  type="button"
                  className="btn-modern btn-modern-light btn-modern-sm"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterYear('');
                    setFilterMonth('');
                    setFilterBranchSearch('');
                    setFilterType('');
                    setFilterClassification('');
                    setPage(1);
                  }}
                  title="Limpar filtros"
                >
                  <FiX size={14} />
                </button>
              )}

              {canManage && selectedIds.length === 1 && (
                <button
                  type="button"
                  className="btn-modern btn-modern-light btn-modern-sm animate__animated animate__fadeIn"
                  onClick={() => {
                    const sel = goals.find(g => g.id === selectedIds[0]);
                    if (sel) openCopyReferencesModal(sel);
                  }}
                  title="Copiar referências, filtros e variáveis da meta selecionada"
                >
                  <FiLayers size={14} className="me-1 text-primary" />
                  Copiar Referências
                </button>
              )}

              {canManage && selectedIds.length > 0 && (
                <button
                  type="button"
                  className="btn-modern btn-modern-light-danger btn-modern-sm animate__animated animate__fadeIn"
                  onClick={() => setShowBulkDeleteModal(true)}
                >
                  <FiTrash2 size={14} className="me-1" />
                  Excluir ({selectedIds.length})
                </button>
              )}
            </div>
          </div>
          <div className="card-body-modern p-4 pt-0" style={{ position: 'relative', zIndex: 1 }}>
            {loading && sortedGoals.length > 0 && (
              <Loading.Overlay text="Atualizando metas..." size="sm" />
            )}
            <div className="table-responsive">
              <table className="table-modern w-100">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={sortedGoals.length > 0 && selectedIds.length === sortedGoals.length}
                        onChange={() => toggleSelectAll(sortedGoals)}
                      />
                    </th>
                    <th className="sortable-header" style={{ width: '60px' }} onClick={() => handleSort('id')}>
                      ID{renderSortIcon('id')}
                    </th>
                    <th className="sortable-header" style={{ minWidth: '130px' }} onClick={() => handleSort('branch')}>
                      Filial{renderSortIcon('branch')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('vendor')}>
                      Vendedor / Colaborador{renderSortIcon('vendor')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('goal_type')}>
                      Tipo{renderSortIcon('goal_type')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('goal_classification')}>
                      Classificação{renderSortIcon('goal_classification')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('period')}>
                      Mês/Ano{renderSortIcon('period')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('filters')}>
                      Filtros{renderSortIcon('filters')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('variables')}>
                      Variáveis{renderSortIcon('variables')}
                    </th>
                    <th className="sortable-header text-end" onClick={() => handleSort('value')}>
                      Valor{renderSortIcon('value')}
                    </th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && sortedGoals.length === 0 ? (
                    <Loading.Table
                      colSpan={11}
                      text="Carregando metas de vendas..."
                      subtext="Buscando metas, filiais, presets e variáveis associadas..."
                    />
                  ) : sortedGoals.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-5 text-muted small">
                        Nenhuma meta cadastrada para os filtros selecionados.
                      </td>
                    </tr>
                  ) : (
                    sortedGoals.map((g) => (
                    <React.Fragment key={g.id}>
                      <tr style={g.has_next_month ? { backgroundColor: '#f0fdf4' } : {}}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(g.id)}
                            onChange={() => toggleSelectOne(g.id)}
                          />
                        </td>
                        <td><code style={{ fontSize:'0.7rem' }}>{g.id}</code></td>
                        <td>
                          {resolveGoalBranchBadge(g)}
                        </td>
                        <td>
                          <div className="fw-medium">
                            {g.vendor_name || g.employee_name || (g.preset_name ? <span className="text-primary fw-semibold">{g.preset_name}</span> : (g.branch_name ? <span className="text-primary fw-semibold">{g.branch_name}</span> : 'Meta Geral / Loja'))}
                          </div>
                        </td>
                        <td><span className="badge-modern badge-modern-secondary">{getGoalTypeLabel(g.goal_type, g.goal_subtype)}</span></td>
                        <td>{getGoalClassificationBadge(g.goal_classification)}</td>
                        <td>
                          <span className="text-muted small">{periodLabel(g)}</span>
                          {g.has_next_month && (
                            <span className="ms-1" title="Próximo mês já cadastrado" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                          )}
                        </td>
                        <td>{resolveFilterNames(g)}</td>
                        <td>{resolveVariableNames(g)}</td>
                        <td className="text-end fw-semibold">{isQuantitySubtype(g.goal_subtype) ? `${Number(g.target_quantity || 0).toLocaleString('pt-BR')} un` : Number(g.target_amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                        <td>
                          <div className="d-flex justify-content-end align-items-center gap-2">
                            <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => toggleExpand(g.id)} title="Expandir">
                              {expandedGoalId === g.id ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                            </button>
                            {canManage ? (
                              <>
                                <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => openCopyReferencesModal(g)} title="Copiar referências para outras metas" style={{ padding: '6px 10px' }}><FiLayers size={14} /></button>
                                <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => replicate(g)} title="Copiar meta" style={{ padding: '6px 10px' }}><FiCopy size={14} /></button>
                                <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => editRow(g)} title="Editar" style={{ padding: '6px 10px' }}><FiEdit2 size={14} /></button>
                                <button type="button" className="btn-modern btn-modern-light-danger btn-modern-sm" onClick={() => remove(g.id)} title="Excluir" style={{ padding: '6px 10px' }}><FiTrash2 size={14} /></button>
                              </>
                            ) : (
                              <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => editRow(g)} title="Visualizar detalhes" style={{ padding: '6px 10px' }}><FiSearch size={14} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedGoalId === g.id && (
                        <tr>
                          <td colSpan="11" className="p-0">
                            <div className="bg-light border-top" style={{ padding: '16px 20px' }}>
                              <h6 className="mb-3 text-dark fw-semibold">Detalhes Adicionais</h6>
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid var(--primary)' }}>
                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>{g.vendor_name ? 'Vendedor' : (g.employee_name ? 'Funcionário' : 'Colaborador / Escopo')}</small>
                                    <div className="fw-semibold text-dark mt-1">{g.vendor_name || g.employee_name || (g.preset_name || 'Meta Geral')}</div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid #f59e0b' }}>
                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Filial Vinculada</small>
                                    <div className="mt-1 d-flex align-items-center gap-2">
                                      {resolveGoalBranchBadge(g)}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid #6366f1' }}>
                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Classificação da Meta</small>
                                    <div className="mt-1">{getGoalClassificationBadge(g.goal_classification)}</div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid var(--info)' }}>
                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Período de Referência</small>
                                    <div className="fw-semibold text-dark mt-1">{periodLabel(g)}</div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid var(--success)' }}>
                                    <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>{isQuantitySubtype(g.goal_subtype) ? 'Meta (Quantidade)' : 'Valor da Meta'}</small>
                                    <div className="fw-bold text-dark mt-1" style={{ fontSize: '1rem' }}>
                                      {isQuantitySubtype(g.goal_subtype) ? `${Number(g.target_quantity || 0).toLocaleString('pt-BR')} un` : Number(g.target_amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </div>
                                  </div>
                                </div>
                                {isQuantitySubtype(g.goal_subtype) && (
                                  <>
                                    <div className="col-md-6">
                                      <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid var(--info)' }}>
                                        <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>{g.goal_subtype === 'clientes_positivados' ? 'Valor por Cliente' : 'Valor por Unidade'}</small>
                                        <div className="fw-semibold text-dark mt-1">{Number(g.unit_value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4 })}</div>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid var(--success)' }}>
                                        <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Bônus (se atingir a meta)</small>
                                        <div className="fw-semibold text-dark mt-1">{Number(g.bonus_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                                      </div>
                                    </div>
                                  </>
                                )}
                                {g.notes && (
                                  <div className="col-12">
                                    <div className="bg-white p-3 rounded border" style={{ borderLeft: '4px solid var(--warning)' }}>
                                      <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>Observações</small>
                                      <p className="mb-0 text-muted small mt-1">{g.notes}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                  </React.Fragment>
                )))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <BIPagination
            page={page}
            perPage={perPage}
            total={totalRecords}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            itemLabel="metas"
            perPageOptions={[10, 25, 50, 100, 200]}
          />
        </div>
      </div>
    </div>

    {showModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
        >
          <div
            style={{
              background: 'white', borderRadius: '20px', width: '98%',
              maxWidth: '95vw', maxHeight: '96vh',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
              animation: 'modalSlideIn 0.25s ease',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px 28px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'white', zIndex: 10, flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: 'var(--primary)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiTarget size={20} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1rem' }}>
                    {form.id ? 'Editar Meta' : 'Nova Meta'}
                  </h5>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {form.id ? 'Atualize os dados da meta de faturamento' : 'Configure uma nova meta para o vendedor'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '8px' }}
              >
                <FiX size={20} color="var(--text-muted)" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                <BIVendorSelectSearch
                  label="Vendedor"
                  required={!form.employee_id && selectedPreset?.target_type !== 'employee'}
                  value={form.vendor_id}
                  onChange={handleVendorChange}
                />

                <BIEmployeeSelectSearch
                  label={selectedPreset?.target_type === 'employee' || form.employee_id ? 'Funcionário' : 'Funcionário (opcional)'}
                  required={Boolean(selectedPreset?.target_type === 'employee' || (!form.vendor_id && form.employee_id))}
                  value={form.employee_id}
                  onChange={handleEmployeeChange}
                />

                <BIGoalGroupSelectSearch
                  label="Grupo de Metas"
                  required
                  value={selectedPresetId}
                  onChange={handleApplyPreset}
                  presets={goalPresets}
                  currentVendorId={form.vendor_id}
                  currentEmployeeId={form.employee_id}
                  vendorCodusurMap={vendorCodusurMap}
                  getGoalClassificationLabel={getGoalClassificationLabel}
                  getGoalSubtypeLabel={getGoalSubtypeLabel}
                  onClear={() => {
                    setSelectedPresetId('');
                    setForm(prev => ({ ...prev, filter_ids: [], variable_ids: [] }));
                  }}
                />

                {suggestedPreset && (!selectedPresetId || String(suggestedPreset.id) !== String(selectedPresetId)) && (
                  <div className="alert alert-info py-2 px-3 mb-3 d-flex justify-content-between align-items-center" style={{ fontSize: '0.82rem', borderRadius: '8px' }}>
                    <span>
                      <FiFolder className="me-1" />
                      Grupo de Metas sugerido: <strong>{suggestedPreset.name}</strong> ({getGoalClassificationLabel(suggestedPreset.goal_classification)}{suggestedPreset.goal_subtype ? ` • ${getGoalSubtypeLabel(suggestedPreset.goal_subtype)}` : ''} | {suggestedPreset.filter_ids_array?.length || 0} filtros, {suggestedPreset.variable_ids_array?.length || 0} variáveis)
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary py-1 px-2"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => handleApplyPreset(suggestedPreset)}
                    >
                      Aplicar Grupo
                    </button>
                  </div>
                )}

                {/* Card com regras de Filtros e Variáveis aplicadas pelo Grupo de Metas */}
                {selectedPreset ? (
                  <div style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '16px'
                  }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                        <FiFolder className="me-1 text-primary" /> Regras aplicadas: <strong>{selectedPreset.name}</strong>
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '0.72rem' }}>
                          {getGoalClassificationLabel(selectedPreset.goal_classification)}
                        </span>
                        {selectedPreset.goal_subtype ? (
                          <span className="badge" style={{
                            fontSize: '0.72rem',
                            backgroundColor: selectedPreset.goal_subtype === 'clientes_positivados' ? '#f0f9ff' : '#f5f3ff',
                            color: selectedPreset.goal_subtype === 'clientes_positivados' ? '#0284c7' : '#7c3aed',
                            border: `1px solid ${selectedPreset.goal_subtype === 'clientes_positivados' ? '#bae6fd' : '#ddd6fe'}`
                          }}>
                            {getGoalSubtypeLabel(selectedPreset.goal_subtype)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="row g-2" style={{ fontSize: '0.78rem' }}>
                      <div className="col-md-6">
                        <div style={{ color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          Filtros vinculados ({presetFilterNames.length}):
                        </div>
                        {presetFilterNames.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                            {presetFilterNames.map((name, i) => (
                              <span key={i} style={{
                                backgroundColor: '#eff6ff',
                                color: '#1d4ed8',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                border: '1px solid #bfdbfe'
                              }}>
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted fst-italic">Nenhum filtro vinculado</span>
                        )}
                      </div>
                      <div className="col-md-6">
                        <div style={{ color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          Variáveis de cálculo ({presetVariableNames.length}):
                        </div>
                        {presetVariableNames.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                            {presetVariableNames.map((name, i) => (
                              <span key={i} style={{
                                backgroundColor: '#f5f3ff',
                                color: '#6d28d9',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                border: '1px solid #ddd6fe'
                              }}>
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted fst-italic">Nenhuma variável vinculada</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-muted" style={{ fontSize: '0.72rem' }}>
                      🔒 Filtros e variáveis gerenciados exclusivamente através do Grupo de Metas selecionado.
                    </div>
                  </div>
                ) : (form.filter_ids?.length > 0 || form.variable_ids?.length > 0) ? (
                  <div style={{
                    backgroundColor: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    fontSize: '0.8rem',
                    color: '#92400e'
                  }}>
                    <div className="fw-semibold mb-1">
                      <FiFolder className="me-1" /> Meta com {form.filter_ids?.length || 0} filtros e {form.variable_ids?.length || 0} variáveis preexistentes
                    </div>
                    <div>
                      Selecione um <strong>Grupo de Metas</strong> acima para padronizar esta meta com as regras oficiais.
                    </div>
                  </div>
                ) : (
                  <div style={{
                    backgroundColor: '#f1f5f9',
                    border: '1px dashed #cbd5e1',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    fontSize: '0.8rem',
                    color: '#64748b',
                    textAlign: 'center'
                  }}>
                    <FiFolder className="me-1 text-primary" /> Selecione o <strong>Grupo de Metas</strong> acima para carregar automaticamente os filtros e variáveis correspondentes.
                  </div>
                )}

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div className="form-group-modern mb-0">
                      <label>Classificação da Meta</label>
                      <select 
                        className="form-select-modern" 
                        value={form.goal_classification || 'vendedor'} 
                        onChange={e => handleClassificationChange(e.target.value)}
                      >

                        <option value="vendedor">Vendedor</option>
                        <option value="bijou">Bijou</option>
                        <option value="operador_caixa">Operador de Caixa</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group-modern mb-0">
                      <label>Tipo de Meta</label>
                      <select 
                        className="form-select-modern" 
                        value={form.goal_type} 
                        onChange={e => setForm({...form, goal_type: e.target.value})}
                      >
                        <option value="monthly_revenue">Mensal</option>
                        <option value="quarterly_revenue">Trimestral</option>
                        <option value="yearly_revenue">Anual</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group-modern mb-0">
                      <label>Subtipo</label>
                      <select 
                        className="form-select-modern" 
                        value={form.goal_subtype} 
                        onChange={e => handleSubtypeChange(e.target.value)}
                      >
                        <option value="">Vendas (padrão)</option>
                        <option value="clientes_positivados">Clientes Positivados</option>
                        <option value="produtos_destaque">Produtos Destaque</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="form-group-modern mb-0">
                      <label>Ano de Referência</label>
                      <input 
                        type="number" 
                        className="form-control-modern" 
                        value={form.reference_year} 
                        onChange={e => setForm({...form, reference_year: e.target.value})}
                        placeholder="Ex: 2024"
                      />
                    </div>
                  </div>
                </div>

                {(form.goal_type === 'monthly_revenue' || form.goal_type === 'quarterly_revenue') && (
                  <div className="form-group-modern mb-3">
                    <label>{form.goal_type === 'monthly_revenue' ? 'Mês de Referência' : 'Trimestre'}</label>
                    <select 
                      className="form-select-modern" 
                      value={form.reference_month} 
                      onChange={e => setForm({...form, reference_month: e.target.value})}
                    >
                      <option value="">Selecione (Opcional)</option>
                      {form.goal_type === 'monthly_revenue' 
                        ? MESES.map(m => <option key={m.v} value={m.v}>{m.n}</option>)
                        : [1,2,3,4].map(q => <option key={q} value={q}>{q}º Trimestre (T{q})</option>)
                      }
                    </select>
                  </div>
                )}

                {isQuantitySubtype(form.goal_subtype) ? (
                  <div className="form-group-modern mb-3">
                    <label>Meta (Quantidade)</label>
                    <input
                      type="number"
                      className="form-control-modern"
                      min="0"
                      step="1"
                      value={form.target_quantity}
                      onChange={e => setForm({...form, target_quantity: e.target.value})}
                      placeholder="Ex: 50 clientes"
                    />
                  </div>
                ) : (
                  <div className="form-group-modern mb-3">
                    <label>Valor da Meta (R$)</label>
                    <input 
                      type="text" 
                      className="form-control-modern" 
                      required 
                      value={form.target_amount} 
                      onChange={e => setForm({...form, target_amount: formatCurrencyBRL(e.target.value)})}
                      placeholder="R$ 0,00"
                    />
                  </div>
                )}

                {form.goal_subtype === 'produtos_destaque' && (
                  <div className="form-group-modern mb-3">
                    <ProductMultiSelect
                      label="Produtos"
                      value={form.product_ids}
                      onChange={val => setForm({...form, product_ids: val})}
                    />
                  </div>
                )}

                {isQuantitySubtype(form.goal_subtype) && (
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <div className="form-group-modern mb-0">
                        <label>{form.goal_subtype === 'clientes_positivados' ? 'Valor por Cliente (R$)' : 'Valor por Unidade (R$)'}</label>
                        <input
                          type="number"
                          className="form-control-modern"
                          step="0.0001"
                          min="0"
                          value={form.unit_value}
                          onChange={e => setForm({...form, unit_value: e.target.value})}
                          placeholder="Ex: 1,5000"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group-modern mb-0">
                        <label>Bônus ao Atingir a Meta (R$)</label>
                        <input
                          type="text"
                          className="form-control-modern"
                          value={form.bonus_amount}
                          onChange={e => setForm({...form, bonus_amount: formatCurrencyBRL(e.target.value)})}
                          placeholder="R$ 0,00"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group-modern mb-0">
                  <label>Observações / Notas</label>
                  <textarea 
                    className="form-control-modern" 
                    rows="3"
                    value={form.notes} 
                    onChange={e => setForm({...form, notes: e.target.value})}
                    placeholder="Notas internas opcionais..."
                    style={{ resize: 'none' }}
                  />
                </div>

                {/* ===== VALORES DE REFERÊNCIA PARA O BI (OPCIONAL) ===== */}
                <div style={{
                  border: '1px solid var(--border, #e2e8f0)',
                  borderRadius: '10px',
                  padding: '16px',
                  backgroundColor: 'rgba(248, 250, 252, 0.8)',
                  marginTop: '18px',
                  marginBottom: '18px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '1.1rem' }}>🎯</span>
                      <h6 style={{ fontWeight: 600, fontSize: '0.88rem', margin: 0, color: 'var(--text-main, #1e293b)' }}>
                        Valores de Referência para o BI (Opcional)
                      </h6>
                    </div>
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor: '#e0f2fe',
                      color: '#0369a1',
                      border: '1px solid #bae6fd'
                    }}>
                      Indicadores de Atenção
                    </span>
                  </div>
                  <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '14px', lineHeight: 1.4 }}>
                    Defina valores de referência para que o BI sinalize pontos de atenção e acompanhe o ritmo através de indicadores visuais de cores (verde, amarelo e vermelho).
                  </p>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="form-group-modern mb-0">
                        <label style={{ fontSize: '0.75rem', fontWeight: 600 }}>Venda Líquida Diária (R$)</label>
                        <input
                          type="text"
                          className="form-control-modern"
                          value={form.ref_daily_net_sales}
                          onChange={e => setForm({ ...form, ref_daily_net_sales: formatCurrencyBRL(e.target.value) })}
                          placeholder="R$ 0,00"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group-modern mb-0">
                        <label style={{ fontSize: '0.75rem', fontWeight: 600 }}>Qtd de Vendas Diárias por Vendedor</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="form-control-modern"
                          value={form.ref_daily_orders_qty}
                          onChange={e => {
                            const val = e.target.value.replace(/[^0-9.,]/g, '');
                            setForm({ ...form, ref_daily_orders_qty: val });
                          }}
                          placeholder="Ex: 20,0"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group-modern mb-0">
                        <label style={{ fontSize: '0.75rem', fontWeight: 600 }}>Ticket Médio (R$)</label>
                        <input
                          type="text"
                          className="form-control-modern"
                          value={form.ref_average_ticket}
                          onChange={e => setForm({ ...form, ref_average_ticket: formatCurrencyBRL(e.target.value) })}
                          placeholder="R$ 0,00"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group-modern mb-0">
                        <label style={{ fontSize: '0.75rem', fontWeight: 600 }}>IA (Itens por Atendimento)</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="form-control-modern"
                          value={form.ref_items_per_attendance}
                          onChange={e => {
                            const val = e.target.value.replace(/[^0-9.,]/g, '');
                            setForm({ ...form, ref_items_per_attendance: val });
                          }}
                          placeholder="Ex: 3,96"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group-modern mb-0">
                        <label style={{ fontSize: '0.75rem', fontWeight: 600 }}>VIA (Valor do Item por Atendimento R$)</label>
                        <input
                          type="text"
                          className="form-control-modern"
                          value={form.ref_item_value_per_attendance}
                          onChange={e => setForm({ ...form, ref_item_value_per_attendance: formatCurrencyBRL(e.target.value) })}
                          placeholder="R$ 0,00"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ===== VALIDAÇÃO DOS FILTROS VINCULADOS ===== */}
                {form.filter_ids.length > 0 && (() => {
                  const goalFilterObjs = form.filter_ids.map(fid => {
                    const fBase = filters.find(ff => Number(ff.id) === Number(fid));
                    if (!fBase) return null;
                    const customCfg = form.filter_configs?.[fBase.id];
                    return customCfg ? { ...fBase, ...customCfg, id: fBase.id, filter_id: fBase.id } : fBase;
                  }).filter(Boolean);
                  const editingGoalFiltersCount = goalFilterObjs.filter(f => editingFilterIds.includes(Number(f.filter_id || f.id))).length;
                  return (
                    <div className="mb-3" style={{ borderTop: '1px solid var(--border)', paddingTop: '20px', marginTop: '20px' }}>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <div>
                          <h6 style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '2px' }}>
                            Validação dos Filtros Vinculados
                          </h6>
                          <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                            Período, comissão e bônus de cada filtro vinculado a esta meta.
                          </p>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                          {editingGoalFiltersCount > 0 ? (
                            <>
                              <button
                                type="button"
                                className="btn-modern btn-modern-primary btn-modern-sm"
                                disabled={savingFilterValid}
                                onClick={() => requestSaveAllFiltersValidation(goalFilterObjs)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', fontSize: '0.75rem' }}
                              >
                                <FiCheck size={13} />
                                {savingFilterValid ? 'Salvando...' : `Salvar Todos os Filtros (${editingGoalFiltersCount})`}
                              </button>
                              <button
                                type="button"
                                className="btn-modern btn-modern-light btn-modern-sm"
                                disabled={savingFilterValid}
                                onClick={() => cancelEditAllFilters(goalFilterObjs)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', fontSize: '0.75rem' }}
                              >
                                <FiX size={13} /> Cancelar Edição
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn-modern btn-modern-light btn-modern-sm"
                              onClick={() => startEditAllFilters(goalFilterObjs)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', fontSize: '0.75rem' }}
                              title="Editar todos os filtros vinculados simultaneamente"
                            >
                              <FiEdit2 size={12} /> Editar Todos os Filtros
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table-modern w-100" style={{ fontSize: '0.7rem' }}>
                          <thead>
                            <tr>
                              <th>Filtro</th>
                              <th title="Data início do período do filtro">Início</th>
                              <th title="Data fim do período do filtro">Fim</th>
                              <th title="Percentual de comissão">%</th>
                              <th title="Comissão fixa por unidade">Fixo/Un</th>
                              <th title="Quantidade mínima de produtos">Qtd Mín</th>
                              <th title="Valor mínimo de venda">Valor Mín</th>
                              <th title="Usar valor da meta como mínimo">Meta</th>
                              <th title="Exibir porcentagem no relatório">%Rel</th>
                              <th title="Bônus: qtd / valor / valor bônus">Bônus</th>
                              <th title="Incluir no total / Incluir comissão no total">Incluir</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {form.filter_ids.map(fid => {
                              const fBase = filters.find(ff => Number(ff.id) === Number(fid));
                              if (!fBase) return null;
                              const customCfg = form.filter_configs?.[fBase.id];
                              const isCustom = Boolean(customCfg && (customCfg.is_custom === 1 || customCfg.is_custom === true));
                              const f = customCfg ? { ...fBase, ...customCfg, id: fBase.id, filter_id: fBase.id } : fBase;
                              const isEditing = editingFilterIds.includes(Number(f.id)) || editingFilterId === Number(f.id);
                              const fEdit = filtersEditData[f.id] || (editingFilterId === Number(f.id) ? filterEditData : {}) || {};
                              const fmtDate = (d) => d ? d.slice(0,10).split('-').reverse().join('/') : null;
                              return (
                                <tr key={f.id} onKeyDown={(e) => { if (e.key === 'Enter' && isEditing) { e.preventDefault(); requestSaveFilterValidation(f); } }}>
                                  <td>
                                    <div className="d-flex align-items-center flex-wrap gap-1">
                                      <span className="fw-medium">{f.name}</span>
                                      {isCustom && (
                                        <span
                                          className="badge"
                                          style={{ background: '#ecfdf5', color: '#047857', fontSize: '0.62rem', border: '1px solid #a7f3d0' }}
                                          title="Este filtro possui valores de referência configurados exclusivamente para esta meta"
                                        >
                                          ⚡ Exclusivo da Meta
                                        </span>
                                      )}
                                    </div>
                                    {(() => {
                                      const matchingRule = (variables || [])
                                        .filter(vr => (form.variable_ids || []).includes(Number(vr.id)))
                                        .flatMap(vr => vr.accrual_rules || [])
                                        .find(r => r.reference_type === 'filter' && Number(r.reference_id) === Number(f.id));
                                      if (!matchingRule) return null;
                                      const bParts = [];
                                      if (Number(matchingRule.accrual_percent || 0) > 0) bParts.push(`+${Number(matchingRule.accrual_percent).toFixed(2)}%`);
                                      if (Number(matchingRule.accrual_amount || 0) > 0) bParts.push(`+R$ ${Number(matchingRule.accrual_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
                                      const bText = bParts.join(' ') || '+0%';
                                      return (
                                        <span className="badge ms-1" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.62rem', border: '1px solid #fde68a' }} title="Este filtro possui regra de acréscimo vinculada">
                                          ⚡ Acréscimo: {Number(matchingRule.use_goal_value) === 1 ? 'Meta' : 'R$ ' + Number(matchingRule.threshold || 0).toLocaleString('pt-BR')} ({bText})
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  {isEditing ? (
                                    <>
                                      <td><input type="date" className="form-control-modern" style={{width:'140px'}} value={(((fEdit.start_date ?? f.start_date) || '') === '0000-00-00' ? '' : (fEdit.start_date ?? f.start_date)) ?? ''} onChange={e => updateFilterField(f.id, 'start_date', e.target.value)} /></td>
                                      <td><input type="date" className="form-control-modern" style={{width:'140px'}} value={(((fEdit.end_date ?? f.end_date) || '') === '0000-00-00' ? '' : (fEdit.end_date ?? f.end_date)) ?? ''} onChange={e => updateFilterField(f.id, 'end_date', e.target.value)} /></td>
                                      <td><input type="number" step="0.0001" className="form-control-modern" style={{width:'100px'}} value={fEdit.commission_percent ?? f.commission_percent} onChange={e => updateFilterField(f.id, 'commission_percent', e.target.value)} /></td>
                                      <td><input type="number" step="0.0001" className="form-control-modern" style={{width:'105px'}} value={fEdit.commission_fixed_per_unit ?? f.commission_fixed_per_unit} onChange={e => updateFilterField(f.id, 'commission_fixed_per_unit', e.target.value)} /></td>
                                      <td><input type="number" step="0.01" className="form-control-modern" style={{width:'100px'}} value={fEdit.commission_min_products_qty ?? f.commission_min_products_qty} onChange={e => updateFilterField(f.id, 'commission_min_products_qty', e.target.value)} /></td>
                                      <td><input type="number" step="0.01" className="form-control-modern" style={{width:'120px'}} value={fEdit.commission_min_sold_value ?? f.commission_min_sold_value} onChange={e => updateFilterField(f.id, 'commission_min_sold_value', e.target.value)} /></td>
                                      <td className="text-center"><input type="checkbox" checked={Number(fEdit.commission_use_goal_value ?? f.commission_use_goal_value) === 1} onChange={e => updateFilterField(f.id, 'commission_use_goal_value', e.target.checked ? 1 : 0)} /></td>
                                      <td className="text-center"><input type="checkbox" checked={Number(fEdit.show_commission_percentage ?? f.show_commission_percentage) === 1} onChange={e => updateFilterField(f.id, 'show_commission_percentage', e.target.checked ? 1 : 0)} /></td>
                                      <td>
                                        <div className="d-flex flex-column" style={{gap:'3px'}}>
                                          <div><input type="number" step="0.01" placeholder="Qtd mín" style={{width:'90px'}} value={fEdit.bonus_min_products_qty ?? f.bonus_min_products_qty ?? ''} onChange={e => updateFilterField(f.id, 'bonus_min_products_qty', e.target.value)} /><span style={{fontSize:'0.65rem',marginLeft:'3px'}}>qtd</span></div>
                                          <div><input type="number" step="0.01" placeholder="Valor mín" style={{width:'90px'}} value={fEdit.bonus_min_sold_value ?? f.bonus_min_sold_value ?? ''} onChange={e => updateFilterField(f.id, 'bonus_min_sold_value', e.target.value)} /><span style={{fontSize:'0.65rem',marginLeft:'3px'}}>R$</span></div>
                                          <div><input type="number" step="0.01" placeholder="Bônus" style={{width:'90px'}} value={fEdit.bonus_amount ?? f.bonus_amount ?? ''} onChange={e => updateFilterField(f.id, 'bonus_amount', e.target.value)} /><span style={{fontSize:'0.65rem',marginLeft:'3px'}}>R$</span></div>
                                        </div>
                                      </td>
                                      <td>
                                        <label style={{fontSize:'0.65rem', display:'block'}}><input type="checkbox" checked={Number(fEdit.include_in_goal_total ?? f.include_in_goal_total) === 1} onChange={e => updateFilterField(f.id, 'include_in_goal_total', e.target.checked ? 1 : 0)} /> Incluir no Total</label>
                                        <label style={{fontSize:'0.65rem', display:'block'}}><input type="checkbox" checked={Number(fEdit.include_commission_in_goal_total ?? f.include_commission_in_goal_total) === 1} onChange={e => updateFilterField(f.id, 'include_commission_in_goal_total', e.target.checked ? 1 : 0)} /> Incluir Comissão</label>
                                      </td>
                                      <td>
                                        <div className="d-flex gap-1">
                                          <button type="button" className="btn-modern btn-modern-primary btn-modern-sm" disabled={savingFilterValid} onClick={(e) => { e.preventDefault(); requestSaveFilterValidation(f); }} style={{padding:'4px 12px', fontSize:'0.75rem'}}>{savingFilterValid ? '…' : 'Salvar'}</button>
                                          <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => cancelEditFilter(f.id)} style={{padding:'4px 12px', fontSize:'0.75rem'}}>Cancelar</button>
                                        </div>
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td>{fmtDate(f.start_date) || '—'}</td>
                                      <td>{fmtDate(f.end_date) || '—'}</td>
                                      <td>{Number(f.commission_percent).toFixed(2)}%</td>
                                      <td>{Number(f.commission_fixed_per_unit).toFixed(4)}</td>
                                      <td>{Number(f.commission_min_products_qty).toFixed(0)}</td>
                                      <td>{Number(f.commission_min_sold_value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })}</td>
                                      <td className="text-center">{Number(f.commission_use_goal_value) === 1 ? '✓' : '—'}</td>
                                      <td className="text-center">{Number(f.show_commission_percentage) === 1 ? '✓' : '—'}</td>
                                      <td style={{ fontSize: '0.65rem', whiteSpace:'nowrap' }}>
                                        {f.bonus_min_products_qty != null && Number(f.bonus_min_products_qty) > 0 ? `${Number(f.bonus_min_products_qty).toFixed(0)} qtd` : ''}
                                        {f.bonus_min_products_qty != null && Number(f.bonus_min_products_qty) > 0 && <br/>}
                                        {f.bonus_min_sold_value != null && Number(f.bonus_min_sold_value) > 0 ? Number(f.bonus_min_sold_value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }) : ''}
                                        {(f.bonus_min_sold_value != null && Number(f.bonus_min_sold_value) > 0) && <br/>}
                                        {f.bonus_amount != null && Number(f.bonus_amount) > 0 ? `Bônus: ${Number(f.bonus_amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })}` : '—'}
                                      </td>
                                      <td style={{ fontSize: '0.65rem', whiteSpace:'nowrap' }}>
                                        <span>{Number(f.include_in_goal_total) === 1 ? 'Total: Sim' : 'Total: Não'}</span>
                                        <br/>
                                        <span>{Number(f.include_commission_in_goal_total) === 1 ? 'Comissão: Sim' : 'Comissão: Não'}</span>
                                      </td>
                                      <td>
                                        <div className="d-flex gap-1 align-items-center">
                                          <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => startEditFilter(f)} title="Editar validação" style={{padding:'4px 10px'}}><FiEdit2 size={14} /></button>
                                          {isCustom && (
                                            <button
                                              type="button"
                                              className="btn-modern btn-modern-light btn-modern-sm"
                                              onClick={() => handleRestoreFilterDefault(f.id)}
                                              title="Restaurar padrão do filtro base"
                                              style={{padding:'4px 10px', color: '#d97706'}}
                                            >
                                              <FiRotateCcw size={14} />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* ===== DETALHES DAS VARIÁVEIS VINCULADAS ===== */}
                {form.variable_ids.length > 0 && (
                  <div className="mb-3" style={{ borderTop: '1px solid var(--border)', paddingTop: '20px', marginTop: '20px' }}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                      <div>
                        <h6 style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '2px' }}>
                          Detalhes das Variáveis Vinculadas
                        </h6>
                        <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                          Fórmula, comissão, bônus e filtros vinculados de cada variável.
                        </p>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        {editingVariableIds.length > 0 ? (
                          <>
                            <button
                              type="button"
                              className="btn-modern btn-modern-primary btn-modern-sm"
                              disabled={savingVariable}
                              onClick={saveAllVariablesValidation}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', fontSize: '0.75rem' }}
                            >
                              <FiCheck size={13} />
                              {savingVariable ? 'Salvando...' : `Salvar Todas as Variáveis (${editingVariableIds.length})`}
                            </button>
                            <button
                              type="button"
                              className="btn-modern btn-modern-light btn-modern-sm"
                              disabled={savingVariable}
                              onClick={cancelEditAllVariables}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', fontSize: '0.75rem' }}
                            >
                              <FiX size={13} /> Cancelar Edição
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn-modern btn-modern-light btn-modern-sm"
                            onClick={startEditAllVariables}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', fontSize: '0.75rem' }}
                            title="Editar todas as variáveis vinculadas simultaneamente"
                          >
                            <FiEdit2 size={12} /> Editar Todas as Variáveis
                          </button>
                        )}
                      </div>
                    </div>
                    {form.variable_ids.map(vid => {
                      const v = variables.find(vv => Number(vv.id) === Number(vid));
                      if (!v) return null;
                      const isEditing = editingVariableIds.includes(Number(v.id)) || editingVariableId === Number(v.id);
                      const vEdit = variablesEditData[v.id] || (editingVariableId === Number(v.id) ? variableEditData : {}) || {};
                      return (
                        <div key={v.id} className="bg-white p-3 rounded border mb-2" style={{ borderLeft: '4px solid var(--primary)' }}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <strong style={{ fontSize: '0.85rem' }}>{v.name}</strong>
                              {!isEditing && v.formula && (
                                <code className="ms-2" style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{v.formula}</code>
                              )}
                            </div>
                            <div>
                              {isEditing ? (
                                <div className="d-flex gap-1">
                                  <button type="button" className="btn-modern btn-modern-primary btn-modern-sm" disabled={savingVariable} onClick={() => saveVariableValidation(v)} style={{padding:'3px 10px', fontSize:'0.7rem'}}>{savingVariable ? '…' : 'Salvar'}</button>
                                  <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => cancelEditVariable(v.id)} style={{padding:'3px 10px', fontSize:'0.7rem'}}>X</button>
                                </div>
                              ) : (
                                <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => startEditVariable(v)} title="Editar variável" style={{padding:'3px 8px'}}><FiEdit2 size={12} /></button>
                              )}
                            </div>
                          </div>
                          {isEditing ? (
                            <div className="row g-2" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveVariableValidation(v); } }}>
                              <div className="col-md-12">
                                <label style={{fontSize:'0.7rem', fontWeight:600}}>Fórmula</label>
                                <input type="text" className="form-control-modern" style={{fontSize:'0.78rem'}} value={vEdit.formula ?? v.formula ?? ''} onChange={e => updateVariableField(v.id, 'formula', e.target.value)} />
                              </div>
                              <div className="col-md-3">
                                <label style={{fontSize:'0.7rem', fontWeight:600}}>% Comissão</label>
                                <input type="number" step="0.0001" className="form-control-modern" value={vEdit.commission_percent ?? v.commission_percent ?? ''} onChange={e => updateVariableField(v.id, 'commission_percent', e.target.value)} />
                              </div>
                              <div className="col-md-3">
                                <label style={{fontSize:'0.7rem', fontWeight:600}}>Valor Mín</label>
                                <input type="number" step="0.01" className="form-control-modern" value={vEdit.commission_min_sold_value ?? v.commission_min_sold_value ?? ''} onChange={e => updateVariableField(v.id, 'commission_min_sold_value', e.target.value)} />
                              </div>
                              <div className="col-md-3">
                                <label style={{fontSize:'0.7rem', fontWeight:600}}><input type="checkbox" checked={Number(vEdit.commission_use_goal_value ?? v.commission_use_goal_value) === 1} onChange={e => updateVariableField(v.id, 'commission_use_goal_value', e.target.checked ? 1 : 0)} /> Usar Meta</label>
                              </div>
                              <div className="col-md-3">
                                <label style={{fontSize:'0.7rem', fontWeight:600}}><input type="checkbox" checked={Number(vEdit.use_sold_qty_for_bonus ?? v.use_sold_qty_for_bonus) === 1} onChange={e => updateVariableField(v.id, 'use_sold_qty_for_bonus', e.target.checked ? 1 : 0)} /> Bonus Qtd</label>
                              </div>
                              <div className="col-md-3">
                                <label style={{fontSize:'0.7rem', fontWeight:600}}>Bonus Val. Qtd</label>
                                <input type="number" step="0.01" className="form-control-modern" value={vEdit.bonus_validation_qty ?? v.bonus_validation_qty ?? ''} onChange={e => updateVariableField(v.id, 'bonus_validation_qty', e.target.value)} />
                              </div>
                              <div className="col-md-3">
                                <label style={{fontSize:'0.7rem', fontWeight:600}}>Valor Bônus</label>
                                <input type="number" step="0.01" className="form-control-modern" value={vEdit.variable_bonus_amount ?? v.variable_bonus_amount ?? ''} onChange={e => updateVariableField(v.id, 'variable_bonus_amount', e.target.value)} />
                              </div>
                              <div className="col-md-3">
                                <label style={{fontSize:'0.7rem', fontWeight:600}}><input type="checkbox" checked={Number(vEdit.has_accrual_rule ?? v.has_accrual_rule) === 1} onChange={e => updateVariableField(v.id, 'has_accrual_rule', e.target.checked ? 1 : 0)} /> Acumulo</label>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex flex-wrap gap-3" style={{ fontSize: '0.75rem' }}>
                              <span>Comissão: <strong>{Number(v.commission_percent).toFixed(2)}%</strong></span>
                              <span>Valor Mín: <strong>{Number(v.commission_min_sold_value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })}</strong></span>
                              <span>Usar Meta: <strong>{Number(v.commission_use_goal_value) === 1 ? 'Sim' : 'Não'}</strong></span>
                              <span>Bonus Qtd: <strong>{Number(v.use_sold_qty_for_bonus) === 1 ? 'Sim' : 'Não'}</strong></span>
                              {v.bonus_validation_qty != null && Number(v.bonus_validation_qty) > 0 && <span>Bônus Val. Qtd: <strong>{Number(v.bonus_validation_qty).toFixed(0)}</strong></span>}
                              {v.variable_bonus_amount != null && Number(v.variable_bonus_amount) > 0 && <span>Valor Bônus: <strong>{Number(v.variable_bonus_amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })}</strong></span>}
                              <span>Acumulo: <strong>{Number(v.has_accrual_rule) === 1 ? 'Sim' : 'Não'}</strong></span>
                            </div>
                          )}
                          {/* Linked filters from calculation_variable_filters */}
                          {v.filters && Array.isArray(v.filters) && v.filters.length > 0 && (() => {
                            const varFilterObjs = v.filters.map(fv => {
                              const ffBase = filters.find(x => Number(x.id) === Number(fv.filter_id));
                              if (!ffBase) return null;
                              const customCfg = form.filter_configs?.[ffBase.id];
                              return customCfg ? { ...ffBase, ...customCfg, id: ffBase.id, filter_id: ffBase.id } : ffBase;
                            }).filter(Boolean);
                            const editingVarFiltersCount = varFilterObjs.filter(f => editingFilterIds.includes(Number(f.filter_id || f.id))).length;
                            return (
                              <div className="mt-2">
                                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Filtros vinculados a esta variável:</span>
                                  <div className="d-flex gap-1">
                                    {editingVarFiltersCount > 0 ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn-modern btn-modern-primary btn-modern-sm"
                                          disabled={savingFilterValid}
                                          onClick={() => requestSaveAllFiltersValidation(varFilterObjs)}
                                          style={{ padding: '2px 8px', fontSize: '0.65rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        >
                                          <FiCheck size={11} /> Salvar Filtros ({editingVarFiltersCount})
                                        </button>
                                        <button
                                          type="button"
                                          className="btn-modern btn-modern-light btn-modern-sm"
                                          disabled={savingFilterValid}
                                          onClick={() => cancelEditAllFilters(varFilterObjs)}
                                          style={{ padding: '2px 8px', fontSize: '0.65rem' }}
                                        >
                                          Cancelar
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn-modern btn-modern-light btn-modern-sm"
                                        onClick={() => startEditAllFilters(varFilterObjs)}
                                        style={{ padding: '2px 8px', fontSize: '0.65rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        title="Editar todos os filtros desta variável simultaneamente"
                                      >
                                        <FiEdit2 size={11} /> Editar Todos os Filtros
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="table-responsive mt-1">
                                  <table className="table-modern w-100" style={{ fontSize: '0.68rem' }}>
                                    <thead>
                                      <tr>
                                        <th>Filtro</th>
                                        <th>Início</th>
                                        <th>Fim</th>
                                        <th>%</th>
                                        <th>Fixo</th>
                                        <th>Qtd Mín</th>
                                        <th>Valor Mín</th>
                                        <th>Meta</th>
                                        <th>%Rel</th>
                                        <th>Bônus</th>
                                        <th>Incluir</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {v.filters.map(fv => {
                                        const ffBase = filters.find(x => Number(x.id) === Number(fv.filter_id));
                                        if (!ffBase) return null;
                                        const customCfg = form.filter_configs?.[ffBase.id];
                                        const isCustom = Boolean(customCfg && (customCfg.is_custom === 1 || customCfg.is_custom === true));
                                        const ff = customCfg ? { ...ffBase, ...customCfg, id: ffBase.id, filter_id: ffBase.id } : ffBase;
                                        const isSubEditing = editingFilterIds.includes(Number(ff.id)) || editingFilterId === Number(ff.id);
                                        const ffEdit = filtersEditData[ff.id] || (editingFilterId === Number(ff.id) ? filterEditData : {}) || {};
                                        const fmtDate = (d) => d ? d.slice(0,10).split('-').reverse().join('/') : null;
                                        return (
                                          <tr key={fv.id} onKeyDown={(e) => { if (e.key === 'Enter' && isSubEditing) { e.preventDefault(); requestSaveFilterValidation(ff); } }}>
                                            <td>
                                              <div className="d-flex align-items-center flex-wrap gap-1">
                                                <span className="fw-medium">{ff.name}</span>
                                                {fv.placeholder && <code className="ms-1" style={{fontSize:'0.6rem'}}>{fv.placeholder}</code>}
                                                {isCustom && (
                                                  <span
                                                    className="badge ms-1"
                                                    style={{ background: '#ecfdf5', color: '#047857', fontSize: '0.62rem', border: '1px solid #a7f3d0' }}
                                                    title="Valores configurados exclusivamente para esta meta"
                                                  >
                                                    ⚡ Exclusivo da Meta
                                                  </span>
                                                )}
                                              </div>
                                              {(() => {
                                                const accRule = (v.accrual_rules || []).find(r => r.reference_type === 'filter' && Number(r.reference_id) === Number(fv.filter_id));
                                                if (!accRule) return null;
                                                const bParts = [];
                                                if (Number(accRule.accrual_percent || 0) > 0) bParts.push(`+${Number(accRule.accrual_percent).toFixed(2)}%`);
                                                if (Number(accRule.accrual_amount || 0) > 0) bParts.push(`+R$ ${Number(accRule.accrual_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
                                                const bText = bParts.join(' ') || '+0%';
                                                const rHasQty = Boolean(accRule.threshold_quantity && Number(accRule.threshold_quantity) > 0);
                                                const rThresholdVal = Number(accRule.threshold || 0);
                                                const condParts = [];
                                                if (Number(accRule.use_goal_value) === 1) condParts.push('Meta');
                                                else if (rThresholdVal > 0) condParts.push(`R$ ${rThresholdVal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
                                                if (rHasQty) condParts.push(`${accRule.threshold_quantity} un`);
                                                if (condParts.length === 0) condParts.push('R$ 0,00');
                                                return (
                                                  <span className="badge ms-1" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.62rem', border: '1px solid #fde68a' }} title="Este filtro possui regra de acréscimo">
                                                    ⚡ Acréscimo: {condParts.join(' e ')} ({bText})
                                                  </span>
                                                );
                                              })()}
                                            </td>
                                            {isSubEditing ? (
                                              <>
                                                <td><input type="date" className="form-control-modern" style={{width:'130px'}} value={(((ffEdit.start_date ?? ff.start_date) || '') === '0000-00-00' ? '' : (ffEdit.start_date ?? ff.start_date)) ?? ''} onChange={e => updateFilterField(ff.id, 'start_date', e.target.value)} /></td>
                                                <td><input type="date" className="form-control-modern" style={{width:'130px'}} value={(((ffEdit.end_date ?? ff.end_date) || '') === '0000-00-00' ? '' : (ffEdit.end_date ?? ff.end_date)) ?? ''} onChange={e => updateFilterField(ff.id, 'end_date', e.target.value)} /></td>
                                                <td><input type="number" step="0.0001" className="form-control-modern" style={{width:'95px'}} value={ffEdit.commission_percent ?? ff.commission_percent} onChange={e => updateFilterField(ff.id, 'commission_percent', e.target.value)} /></td>
                                                <td><input type="number" step="0.0001" className="form-control-modern" style={{width:'95px'}} value={ffEdit.commission_fixed_per_unit ?? ff.commission_fixed_per_unit} onChange={e => updateFilterField(ff.id, 'commission_fixed_per_unit', e.target.value)} /></td>
                                                <td><input type="number" step="0.01" className="form-control-modern" style={{width:'90px'}} value={ffEdit.commission_min_products_qty ?? ff.commission_min_products_qty} onChange={e => updateFilterField(ff.id, 'commission_min_products_qty', e.target.value)} /></td>
                                                <td><input type="number" step="0.01" className="form-control-modern" style={{width:'110px'}} value={ffEdit.commission_min_sold_value ?? ff.commission_min_sold_value} onChange={e => updateFilterField(ff.id, 'commission_min_sold_value', e.target.value)} /></td>
                                                <td className="text-center"><input type="checkbox" checked={Number(ffEdit.commission_use_goal_value ?? ff.commission_use_goal_value) === 1} onChange={e => updateFilterField(ff.id, 'commission_use_goal_value', e.target.checked ? 1 : 0)} /></td>
                                                <td className="text-center"><input type="checkbox" checked={Number(ffEdit.show_commission_percentage ?? ff.show_commission_percentage) === 1} onChange={e => updateFilterField(ff.id, 'show_commission_percentage', e.target.checked ? 1 : 0)} /></td>
                                                <td>
                                                  <div className="d-flex flex-column" style={{gap:'3px'}}>
                                                    <div><input type="number" step="0.01" placeholder="Qtd mín" style={{width:'80px'}} value={ffEdit.bonus_min_products_qty ?? ff.bonus_min_products_qty ?? ''} onChange={e => updateFilterField(ff.id, 'bonus_min_products_qty', e.target.value)} /><span style={{fontSize:'0.6rem',marginLeft:'2px'}}>qtd</span></div>
                                                    <div><input type="number" step="0.01" placeholder="Valor mín" style={{width:'80px'}} value={ffEdit.bonus_min_sold_value ?? ff.bonus_min_sold_value ?? ''} onChange={e => updateFilterField(ff.id, 'bonus_min_sold_value', e.target.value)} /><span style={{fontSize:'0.6rem',marginLeft:'2px'}}>R$</span></div>
                                                    <div><input type="number" step="0.01" placeholder="Bônus" style={{width:'80px'}} value={ffEdit.bonus_amount ?? ff.bonus_amount ?? ''} onChange={e => updateFilterField(ff.id, 'bonus_amount', e.target.value)} /><span style={{fontSize:'0.6rem',marginLeft:'2px'}}>R$</span></div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <label style={{fontSize:'0.6rem',display:'block'}}><input type="checkbox" checked={Number(ffEdit.include_in_goal_total ?? ff.include_in_goal_total) === 1} onChange={e => updateFilterField(ff.id, 'include_in_goal_total', e.target.checked ? 1 : 0)} /> Tot</label>
                                                  <label style={{fontSize:'0.6rem',display:'block'}}><input type="checkbox" checked={Number(ffEdit.include_commission_in_goal_total ?? ff.include_commission_in_goal_total) === 1} onChange={e => updateFilterField(ff.id, 'include_commission_in_goal_total', e.target.checked ? 1 : 0)} /> Com</label>
                                                </td>
                                                <td>
                                                  <div className="d-flex gap-1">
                                                    <button type="button" className="btn-modern btn-modern-primary btn-modern-sm" disabled={savingFilterValid} onClick={(e) => { e.preventDefault(); e.stopPropagation(); requestSaveFilterValidation(ff); }} style={{padding:'4px 10px',fontSize:'0.7rem'}}>{savingFilterValid ? '…' : 'Salvar'}</button>
                                                    <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => cancelEditFilter(ff.id)} style={{padding:'4px 10px',fontSize:'0.7rem'}}>Cancelar</button>
                                                  </div>
                                                </td>
                                              </>
                                            ) : (
                                              <>
                                                <td>{fmtDate(ff.start_date) || '—'}</td>
                                                <td>{fmtDate(ff.end_date) || '—'}</td>
                                                <td>{Number(ff.commission_percent).toFixed(2)}%</td>
                                                <td>{Number(ff.commission_fixed_per_unit).toFixed(4)}</td>
                                                <td>{Number(ff.commission_min_products_qty).toFixed(0)}</td>
                                                <td>{Number(ff.commission_min_sold_value).toLocaleString('pt-BR',{style:'currency',currency:'BRL',minimumFractionDigits:2})}</td>
                                                <td className="text-center">{Number(ff.commission_use_goal_value) === 1 ? '✓' : '—'}</td>
                                                <td className="text-center">{Number(ff.show_commission_percentage) === 1 ? '✓' : '—'}</td>
                                                <td style={{fontSize:'0.62rem'}}>
                                                  {ff.bonus_min_products_qty != null && Number(ff.bonus_min_products_qty) > 0 ? `${Number(ff.bonus_min_products_qty).toFixed(0)}q` : ''}
                                                  {(ff.bonus_min_products_qty != null && Number(ff.bonus_min_products_qty) > 0) && ff.bonus_min_sold_value != null && Number(ff.bonus_min_sold_value) > 0 ? ' / ' : ''}
                                                  {ff.bonus_min_sold_value != null && Number(ff.bonus_min_sold_value) > 0 ? Number(ff.bonus_min_sold_value).toLocaleString('pt-BR',{style:'currency',currency:'BRL',minimumFractionDigits:2}) : ''}
                                                  {(ff.bonus_amount != null && Number(ff.bonus_amount) > 0) ? ' → ' + Number(ff.bonus_amount).toLocaleString('pt-BR',{style:'currency',currency:'BRL',minimumFractionDigits:2}) : ''}
                                                </td>
                                                <td style={{fontSize:'0.62rem'}}>
                                                  <span>{Number(ff.include_in_goal_total) === 1 ? 'T✓' : 'T—'}</span>
                                                  <span className="ms-1">{Number(ff.include_commission_in_goal_total) === 1 ? 'C✓' : 'C—'}</span>
                                                </td>
                                                <td>
                                                  <div className="d-flex gap-1 align-items-center">
                                                    <button type="button" className="btn-modern btn-modern-light btn-modern-sm" onClick={() => startEditFilter(ff)} title="Editar" style={{padding:'4px 10px'}}><FiEdit2 size={13} /></button>
                                                    {isCustom && (
                                                      <button
                                                        type="button"
                                                        className="btn-modern btn-modern-light btn-modern-sm"
                                                        onClick={() => handleRestoreFilterDefault(ff.id)}
                                                        title="Restaurar padrão do filtro base"
                                                        style={{padding:'4px 10px', color: '#d97706'}}
                                                      >
                                                        <FiRotateCcw size={13} />
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                              </>
                                            )}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Regras de Acréscimo de Adicional da Variável */}
                          <div className="mt-3 p-3 rounded" style={{
                            background: '#fffdf5',
                            border: '1px solid #fde68a',
                            borderLeft: '4px solid #d97706'
                          }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                              <div className="d-flex align-items-center gap-2">
                                <FiTrendingUp size={14} style={{ color: '#d97706' }} />
                                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#92400e' }}>
                                  Regras de Acréscimo de Adicional
                                </span>
                                {v.accrual_rules && v.accrual_rules.length > 0 && (
                                  <span className="badge" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.65rem' }}>
                                    {v.accrual_rules.length} regra{v.accrual_rules.length > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              <div>
                                {editingAccrualVarId === Number(v.id) ? (
                                  <div className="d-flex gap-1">
                                    <button
                                      type="button"
                                      className="btn-modern btn-modern-warning btn-modern-sm"
                                      onClick={addAccrualRuleRow}
                                      style={{ padding: '3px 8px', fontSize: '0.7rem' }}
                                    >
                                      <FiPlus size={12} className="me-1" /> Adicionar Regra
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-modern btn-modern-primary btn-modern-sm"
                                      disabled={savingAccrualRules}
                                      onClick={() => saveAccrualRules(v)}
                                      style={{ padding: '3px 12px', fontSize: '0.7rem' }}
                                    >
                                      {savingAccrualRules ? 'Salvando...' : 'Salvar Regras'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-modern btn-modern-light btn-modern-sm"
                                      onClick={cancelEditAccrualRules}
                                      style={{ padding: '3px 8px', fontSize: '0.7rem' }}
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn-modern btn-modern-light btn-modern-sm"
                                    onClick={() => startEditAccrualRules(v)}
                                    style={{ padding: '3px 8px', fontSize: '0.7rem', color: '#92400e' }}
                                    title="Editar regras de acréscimo"
                                  >
                                    <FiEdit2 size={12} className="me-1" />
                                    {v.accrual_rules && v.accrual_rules.length > 0 ? 'Editar Regras' : 'Configurar Regras'}
                                  </button>
                                )}
                              </div>
                            </div>

                            {editingAccrualVarId === Number(v.id) ? (
                              <div>
                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.7rem' }}>
                                  Configure as metas, quantidades e percentuais extras de acréscimo para esta variável.
                                </small>
                                <div className="table-responsive">
                                  <table className="table-modern w-100" style={{ fontSize: '0.68rem' }}>
                                    <thead>
                                      <tr>
                                        <th style={{ width: '100px' }}>Tipo</th>
                                        <th style={{ minWidth: '180px' }}>Referência</th>
                                        <th style={{ width: '120px' }}>Meta (R$)</th>
                                        <th style={{ width: '85px' }}>Qtd Mín</th>
                                        <th style={{ width: '90px' }}>% Acréscimo</th>
                                        <th style={{ width: '90px' }}>R$ Acréscimo</th>
                                        <th style={{ width: '70px' }} className="text-center">Usar Meta</th>
                                        <th style={{ width: '120px' }}>Início</th>
                                        <th style={{ width: '120px' }}>Fim</th>
                                        <th style={{ width: '35px' }}></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {accrualEditRules.map((rule, idx) => (
                                        <tr key={idx}>
                                          <td>
                                            <select
                                              className="form-control-modern form-control-sm"
                                              style={{ fontSize: '0.7rem', padding: '2px 4px' }}
                                              value={rule.reference_type}
                                              onChange={e => updateAccrualRuleField(idx, 'reference_type', e.target.value)}
                                            >
                                              <option value="filter">Filtro</option>
                                              <option value="variable">Variável</option>
                                              <option value="report">Relatório</option>
                                            </select>
                                          </td>
                                          <td>
                                            <select
                                              className="form-control-modern form-control-sm"
                                              style={{ fontSize: '0.7rem', padding: '2px 4px' }}
                                              value={rule.reference_id}
                                              onChange={e => updateAccrualRuleField(idx, 'reference_id', e.target.value)}
                                            >
                                              <option value="">Selecione...</option>
                                              {rule.reference_type === 'filter'
                                                ? filters.map(f => <option key={f.id} value={f.id}>{f.name}</option>)
                                                : rule.reference_type === 'report'
                                                ? reports.map(r => <option key={r.id} value={r.id}>{r.name || `Relatório #${r.id}`}</option>)
                                                : variables.filter(vr => Number(vr.id) !== Number(v.id)).map(vr => <option key={vr.id} value={vr.id}>{vr.name}</option>)
                                              }
                                            </select>
                                          </td>
                                          <td>
                                            <input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              className="form-control-modern form-control-sm"
                                              style={{ fontSize: '0.7rem', padding: '2px 4px' }}
                                              disabled={Boolean(rule.use_goal_value)}
                                              placeholder={rule.use_goal_value ? 'Usando meta' : '0.00'}
                                              value={rule.use_goal_value ? '' : (rule.threshold ?? '')}
                                              onChange={e => updateAccrualRuleField(idx, 'threshold', e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="number"
                                              step="1"
                                              min="0"
                                              className="form-control-modern form-control-sm"
                                              style={{ fontSize: '0.7rem', padding: '2px 4px' }}
                                              placeholder="Qtd"
                                              value={rule.threshold_quantity ?? ''}
                                              onChange={e => updateAccrualRuleField(idx, 'threshold_quantity', e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              className="form-control-modern form-control-sm"
                                              style={{ fontSize: '0.7rem', padding: '2px 4px' }}
                                              placeholder="0.50"
                                              value={rule.accrual_percent ?? ''}
                                              onChange={e => updateAccrualRuleField(idx, 'accrual_percent', e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              className="form-control-modern form-control-sm"
                                              style={{ fontSize: '0.7rem', padding: '2px 4px' }}
                                              placeholder="0.00"
                                              value={rule.accrual_amount ?? ''}
                                              onChange={e => updateAccrualRuleField(idx, 'accrual_amount', e.target.value)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={Boolean(rule.use_goal_value)}
                                              onChange={e => updateAccrualRuleField(idx, 'use_goal_value', e.target.checked ? 1 : 0)}
                                              style={{ cursor: 'pointer' }}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="date"
                                              className="form-control-modern form-control-sm"
                                              style={{ fontSize: '0.68rem', padding: '2px 4px' }}
                                              value={rule.filter_start_date || ''}
                                              onChange={e => updateAccrualRuleField(idx, 'filter_start_date', e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="date"
                                              className="form-control-modern form-control-sm"
                                              style={{ fontSize: '0.68rem', padding: '2px 4px' }}
                                              value={rule.filter_end_date || ''}
                                              onChange={e => updateAccrualRuleField(idx, 'filter_end_date', e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <button
                                              type="button"
                                              className="btn-modern btn-modern-light-danger btn-modern-sm"
                                              style={{ padding: '2px 5px' }}
                                              onClick={() => removeAccrualRuleRow(idx)}
                                              title="Remover regra"
                                            >
                                              <FiTrash2 size={12} />
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {v.accrual_rules && v.accrual_rules.length > 0 ? (
                                  <div className="table-responsive">
                                    <table className="table-modern w-100" style={{ fontSize: '0.68rem' }}>
                                      <thead>
                                        <tr>
                                          <th>Tipo</th>
                                          <th>Referência</th>
                                          <th>Meta (R$)</th>
                                          <th>Qtd Mín</th>
                                          <th>% Acréscimo</th>
                                          <th>R$ Acréscimo</th>
                                          <th>Usar Meta</th>
                                          <th>Período</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {v.accrual_rules.map((rule, rIdx) => {
                                          let refName = '—';
                                          if (rule.reference_type === 'filter') {
                                            const f = filters.find(flt => Number(flt.id) === Number(rule.reference_id));
                                            refName = f ? f.name : `Filtro #${rule.reference_id}`;
                                          } else if (rule.reference_type === 'variable') {
                                            const vr = variables.find(x => Number(x.id) === Number(rule.reference_id));
                                            refName = vr ? vr.name : `Variável #${rule.reference_id}`;
                                          } else if (rule.reference_type === 'report') {
                                            refName = rule.reference_name || `Relatório #${rule.reference_id}`;
                                          }
                                          const fmtD = (d) => d && d !== '0000-00-00' ? d.slice(0, 10).split('-').reverse().join('/') : null;
                                          return (
                                            <tr key={rIdx}>
                                              <td>
                                                <span className="badge" style={{
                                                  background: rule.reference_type === 'filter' ? '#e0f2fe' : rule.reference_type === 'variable' ? '#f3e8ff' : '#fef3c7',
                                                  color: rule.reference_type === 'filter' ? '#0369a1' : rule.reference_type === 'variable' ? '#7e22ce' : '#92400e',
                                                  fontSize: '0.65rem'
                                                }}>
                                                  {rule.reference_type === 'filter' ? 'Filtro' : rule.reference_type === 'variable' ? 'Variável' : 'Relatório'}
                                                </span>
                                              </td>
                                              <td><strong>{refName}</strong></td>
                                              <td>
                                                {Number(rule.use_goal_value) === 1 ? (
                                                  <span className="badge bg-primary-subtle text-primary" style={{ fontSize: '0.65rem' }}>Usa Meta (R$)</span>
                                                ) : (
                                                  Number(rule.threshold || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
                                                )}
                                              </td>
                                              <td>{rule.threshold_quantity != null && Number(rule.threshold_quantity) > 0 ? `${Number(rule.threshold_quantity)} un` : '—'}</td>
                                              <td>
                                                {Number(rule.accrual_percent || 0) > 0 ? (
                                                  <span className="badge bg-success-subtle text-success fw-bold" style={{ fontSize: '0.68rem' }}>
                                                    +{Number(rule.accrual_percent || 0).toFixed(2)}%
                                                  </span>
                                                ) : '—'}
                                              </td>
                                              <td>
                                                {Number(rule.accrual_amount || 0) > 0 ? (
                                                  <span className="badge bg-success-subtle text-success fw-bold" style={{ fontSize: '0.68rem' }}>
                                                    +{Number(rule.accrual_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })}
                                                  </span>
                                                ) : '—'}
                                              </td>
                                              <td className="text-center">{Number(rule.use_goal_value) === 1 ? '✓' : '—'}</td>
                                              <td>
                                                {fmtD(rule.filter_start_date) || fmtD(rule.filter_end_date)
                                                  ? `${fmtD(rule.filter_start_date) || '...'} até ${fmtD(rule.filter_end_date) || '...'}`
                                                  : '—'}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-muted d-flex align-items-center justify-content-between" style={{ fontSize: '0.72rem' }}>
                                    <span>Nenhuma regra de acréscimo cadastrada para esta variável.</span>
                                    <button
                                      type="button"
                                      className="btn-modern btn-modern-light btn-modern-sm"
                                      onClick={() => startEditAccrualRules(v)}
                                      style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                                    >
                                      <FiPlus size={11} className="me-1" /> Cadastrar Regra
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '16px 28px 24px',
                borderTop: '1px solid var(--border)',
                display: 'flex', justifyContent: 'flex-end', gap: '12px',
                background: 'white', zIndex: 10, flexShrink: 0
              }}>
                <button
                  type="button"
                  className="btn-modern btn-modern-secondary"
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  {canManage ? 'Cancelar' : 'Fechar'}
                </button>
                {canManage && (
                  <button
                    type="submit"
                    className="btn-modern btn-modern-primary"
                    disabled={saving}
                  >
                    {saving ? 'Salvando...' : form.id ? 'Atualizar Meta' : <><FiSave size={16} className="me-1" />Salvar Meta</>}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && goalToDelete && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            {/* Ícone de Aviso */}
            <div style={{
              padding: '32px 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiTrash2 size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                Confirmar Exclusão
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Tem certeza que deseja excluir a meta de
              </p>
              <p style={{ margin: '8px 0 0', fontWeight: 600, fontSize: '1rem', color: '#1e293b' }}>
                "{goalToDelete.vendor_name || goalToDelete.employee_name || 'Meta sem vendedor'}"?
              </p>
            </div>

            {/* Alerta */}
            <div style={{
              padding: '0 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                padding: '12px 16px',
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#92400e'
              }}>
                ⚠️ Esta ação não pode ser desfeita. Todos os dados da meta serão removidos.
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 28px 24px',
              display: 'flex', justifyContent: 'center', gap: '12px'
            }}>
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                <FiTrash2 className="me-2" size={16} />
                {deleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '32px 28px 20px', textAlign: 'center' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiTrash2 size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                Excluir Metas
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Tem certeza que deseja excluir <strong>{selectedIds.length}</strong> metas selecionadas?
              </p>
            </div>

            <div style={{ padding: '0 28px 20px', textAlign: 'center' }}>
              <div style={{ padding: '12px 16px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
                ⚠️ Esta ação não pode ser desfeita e removerá permanentemente as metas.
              </div>
            </div>

            <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={bulkDeleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-danger"
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
              >
                <FiTrash2 className="me-2" size={16} />
                {bulkDeleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            {/* Ícone de Erro */}
            <div style={{
              padding: '32px 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiAlertTriangle size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                {errorModalTitle}
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                {errorMessage}
              </p>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 28px 24px',
              display: 'flex', justifyContent: 'center', gap: '12px'
            }}>
              <button
                type="button"
                className="btn-modern btn-modern-primary"
                onClick={() => setShowErrorModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {filterConfirmModal.open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, backdropFilter: 'blur(4px)', padding: '20px'
          }}
        >
          <div style={{
            background: 'white', borderRadius: '16px',
            width: '100%', maxWidth: '520px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #f1f5f9' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div style={{
                  width: '38px', height: '38px', borderRadius: '10px',
                  background: '#eff6ff', color: '#2563eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiTarget size={20} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                    {filterConfirmModal.isBatch
                      ? `Salvar Valores de ${filterConfirmModal.batchItems?.length || 0} Filtros`
                      : 'Salvar Valores do Filtro'}
                  </h5>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {filterConfirmModal.isBatch
                      ? `${filterConfirmModal.batchItems?.length || 0} filtros editados selecionados para salvar`
                      : filterConfirmModal.filter?.name}
                  </span>
                </div>
              </div>
              <p style={{ margin: '12px 0 0', fontSize: '0.88rem', color: '#475569', lineHeight: '1.5' }}>
                {filterConfirmModal.isBatch
                  ? `Você alterou as configurações de ${filterConfirmModal.batchItems?.length || 0} filtros simultaneamente. Como deseja salvar as alterações?`
                  : 'Você alterou as configurações e valores de referência deste filtro. Como deseja salvar as alterações?'}
              </p>
            </div>

            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: '10px',
                  border: '1.5px solid #a7f3d0',
                  background: '#ecfdf5',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={handleConfirmSaveFilterOnlyGoal}
              >
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <strong style={{ color: '#065f46', fontSize: '0.9rem' }}>
                    ⚡ Salvar Somente para Esta Meta (Recomendado)
                  </strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#047857' }}>
                  {filterConfirmModal.isBatch
                    ? <>Os novos valores serão aplicados <strong>exclusivamente nesta meta</strong> para todos os filtros editados. Os cadastros base permanecem intactos.</>
                    : <>Os novos valores serão aplicados <strong>exclusivamente nesta meta</strong>. O cadastro base do filtro permanece intacto para as outras metas e relatórios.</>}
                </p>
              </div>

              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: '10px',
                  border: '1.5px solid #e2e8f0',
                  background: '#f8fafc',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={handleConfirmSaveFilterAndUpdateBase}
              >
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <strong style={{ color: '#334155', fontSize: '0.9rem' }}>
                    🔄 Salvar para a Meta e Atualizar Filtro Base
                  </strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
                  {filterConfirmModal.isBatch
                    ? <>Atualiza os valores de referência também no <strong>cadastro geral de cada filtro na base</strong> (afeta novas metas e relatórios vinculados a eles).</>
                    : <>Atualiza os valores de referência também no <strong>cadastro geral do filtro</strong> (afeta novas metas e relatórios vinculados a ele).</>}
                </p>
              </div>
            </div>

            <div style={{ padding: '14px 24px 20px', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#fafafa', borderTop: '1px solid #f1f5f9' }}>
              <button
                type="button"
                className="btn-modern btn-modern-light"
                onClick={() => setFilterConfirmModal({ open: false, filter: null, saveData: null, isBatch: false, batchItems: [] })}
                disabled={savingFilterValid}
                style={{ fontSize: '0.85rem' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Copiar Referências e Regras para Outras Metas */}
      {copyRefModal.open && copyRefModal.sourceGoal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
          onClick={() => !copyRefModal.applying && setCopyRefModal(prev => ({ ...prev, open: false }))}
        >
          <div
            style={{
              background: 'white', borderRadius: '16px', width: '96%',
              maxWidth: '1100px', maxHeight: '92vh',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
              animation: 'modalSlideIn 0.25s ease',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'white', flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '42px', height: '42px', borderRadius: '12px',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiLayers size={22} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>
                    Copiar Referências e Regras para Outras Metas
                  </h5>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
                    Replique valores do BI, filtros vinculados, variáveis e parâmetros para metas da mesma natureza.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn-modern btn-modern-light"
                onClick={() => setCopyRefModal(prev => ({ ...prev, open: false }))}
                disabled={copyRefModal.applying}
                style={{ padding: '6px 10px', borderRadius: '8px' }}
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Card Meta de Origem */}
              <div style={{
                background: '#f8fafc',
                border: '1.5px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px 20px',
                position: 'relative'
              }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Meta de Origem (Modelo)
                    </span>
                    <span className="badge bg-light text-dark border" style={{ fontSize: '0.75rem' }}>
                      ID #{copyRefModal.sourceGoal.id}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge-modern badge-modern-secondary" style={{ fontSize: '0.75rem' }}>
                      {getGoalTypeLabel(copyRefModal.sourceGoal.goal_type, copyRefModal.sourceGoal.goal_subtype)}
                    </span>
                    {getGoalClassificationBadge(copyRefModal.sourceGoal.goal_classification)}
                  </div>
                </div>

                <div className="row g-3 align-items-center">
                  <div className="col-md-4">
                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Beneficiário / Alvo:</div>
                    <strong style={{ fontSize: '0.95rem', color: '#1e293b' }}>
                      {copyRefModal.sourceGoal.vendor_name || copyRefModal.sourceGoal.employee_name || (copyRefModal.sourceGoal.branch_name ? copyRefModal.sourceGoal.branch_name : (copyRefModal.sourceGoal.branch_code ? `Filial ${copyRefModal.sourceGoal.branch_code}` : 'Meta Geral'))}
                    </strong>
                    {copyRefModal.sourceGoal.branch_code && (
                      <span className="text-muted small ms-2">(Filial {copyRefModal.sourceGoal.branch_code})</span>
                    )}
                  </div>
                  <div className="col-md-2">
                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Período:</div>
                    <span className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>
                      {periodLabel(copyRefModal.sourceGoal)}
                    </span>
                  </div>
                  <div className="col-md-3">
                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Meta Atual:</div>
                    <span className="fw-bold text-primary" style={{ fontSize: '0.95rem' }}>
                      {isQuantitySubtype(copyRefModal.sourceGoal.goal_subtype)
                        ? `${Number(copyRefModal.sourceGoal.target_quantity || 0).toLocaleString('pt-BR')} un`
                        : Number(copyRefModal.sourceGoal.target_amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                  <div className="col-md-3">
                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Filtros & Variáveis:</div>
                    <span className="text-dark small" title={resolveFilterNames(copyRefModal.sourceGoal)}>
                      {resolveFilterNames(copyRefModal.sourceGoal) || 'Nenhum filtro'}
                    </span>
                  </div>
                </div>

                {/* Resumo rápido dos valores de referência BI */}
                <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #cbd5e1', display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '0.78rem', color: '#475569' }}>
                  <span><strong>Venda Diária:</strong> {copyRefModal.sourceGoal.ref_daily_net_sales ? Number(copyRefModal.sourceGoal.ref_daily_net_sales).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</span>
                  <span><strong>Pedidos Diários:</strong> {copyRefModal.sourceGoal.ref_daily_orders_qty != null ? Number(copyRefModal.sourceGoal.ref_daily_orders_qty).toLocaleString('pt-BR') : '-'}</span>
                  <span><strong>Ticket Médio:</strong> {copyRefModal.sourceGoal.ref_average_ticket ? Number(copyRefModal.sourceGoal.ref_average_ticket).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</span>
                  <span><strong>P.A.:</strong> {copyRefModal.sourceGoal.ref_items_per_attendance != null ? Number(copyRefModal.sourceGoal.ref_items_per_attendance).toLocaleString('pt-BR') : '-'}</span>
                  <span><strong>I.P.A.:</strong> {copyRefModal.sourceGoal.ref_item_value_per_attendance ? Number(copyRefModal.sourceGoal.ref_item_value_per_attendance).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</span>
                </div>
              </div>

              {/* Seção Opções do que Copiar */}
              <div>
                <h6 style={{ fontSize: '0.88rem', fontWeight: 700, color: '#334155', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiSliders size={16} className="text-primary" /> O que deseja copiar para as metas de destino?
                </h6>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
                  gap: '10px'
                }}>
                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px',
                    borderRadius: '8px', border: copyRefModal.options.copy_bi_references ? '1.5px solid #818cf8' : '1px solid #e2e8f0',
                    background: copyRefModal.options.copy_bi_references ? '#eef2ff' : '#ffffff',
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={copyRefModal.options.copy_bi_references}
                      onChange={() => toggleCopyOption('copy_bi_references')}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <strong style={{ fontSize: '0.85rem', color: '#1e293b' }}>Valores de Referência do BI</strong>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: '#64748b' }}>
                        Venda líquida diária, pedidos/dia, ticket médio, P.A. e I.P.A.
                      </p>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px',
                    borderRadius: '8px', border: copyRefModal.options.copy_filters ? '1.5px solid #818cf8' : '1px solid #e2e8f0',
                    background: copyRefModal.options.copy_filters ? '#eef2ff' : '#ffffff',
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={copyRefModal.options.copy_filters}
                      onChange={() => toggleCopyOption('copy_filters')}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <strong style={{ fontSize: '0.85rem', color: '#1e293b' }}>Filtros Vinculados</strong>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: '#64748b' }}>
                        Replica a lista de filtros associados à meta.
                      </p>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px',
                    borderRadius: '8px', border: copyRefModal.options.copy_filter_configs ? '1.5px solid #818cf8' : '1px solid #e2e8f0',
                    background: copyRefModal.options.copy_filter_configs ? '#eef2ff' : '#ffffff',
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={copyRefModal.options.copy_filter_configs}
                      onChange={() => toggleCopyOption('copy_filter_configs')}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <strong style={{ fontSize: '0.85rem', color: '#1e293b' }}>Configurações Exclusivas de Filtros</strong>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: '#64748b' }}>
                        Comissões personalizadas, mínimos e bonificações da meta.
                      </p>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px',
                    borderRadius: '8px', border: copyRefModal.options.copy_variables ? '1.5px solid #818cf8' : '1px solid #e2e8f0',
                    background: copyRefModal.options.copy_variables ? '#eef2ff' : '#ffffff',
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={copyRefModal.options.copy_variables}
                      onChange={() => toggleCopyOption('copy_variables')}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <strong style={{ fontSize: '0.85rem', color: '#1e293b' }}>Variáveis Vinculadas</strong>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: '#64748b' }}>
                        Associa as mesmas variáveis de cálculo de comissão.
                      </p>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px',
                    borderRadius: '8px', border: copyRefModal.options.copy_bonus_params ? '1.5px solid #818cf8' : '1px solid #e2e8f0',
                    background: copyRefModal.options.copy_bonus_params ? '#eef2ff' : '#ffffff',
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={copyRefModal.options.copy_bonus_params}
                      onChange={() => toggleCopyOption('copy_bonus_params')}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <strong style={{ fontSize: '0.85rem', color: '#1e293b' }}>Parâmetros de Bonificação</strong>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: '#64748b' }}>
                        Valor unitário, bônus e produtos de destaque selecionados.
                      </p>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px',
                    borderRadius: '8px', border: copyRefModal.options.copy_target_value ? '1.5px solid #818cf8' : '1px solid #e2e8f0',
                    background: copyRefModal.options.copy_target_value ? '#eef2ff' : '#ffffff',
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={copyRefModal.options.copy_target_value}
                      onChange={() => toggleCopyOption('copy_target_value')}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <strong style={{ fontSize: '0.85rem', color: '#1e293b' }}>Valor / Qtd da Meta</strong>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: '#64748b' }}>
                        Substituir valor/quantidade alvo das metas de destino.
                      </p>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px',
                    borderRadius: '8px', border: copyRefModal.options.copy_notes ? '1.5px solid #818cf8' : '1px solid #e2e8f0',
                    background: copyRefModal.options.copy_notes ? '#eef2ff' : '#ffffff',
                    cursor: 'pointer', transition: 'all 0.15s'
                  }}>
                    <input
                      type="checkbox"
                      checked={copyRefModal.options.copy_notes}
                      onChange={() => toggleCopyOption('copy_notes')}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <strong style={{ fontSize: '0.85rem', color: '#1e293b' }}>Observações / Anotações</strong>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: '#64748b' }}>
                        Copiar o texto do campo de observações.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Seção Filtros e Seleção de Metas de Destino */}
              <div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div>
                    <h6 style={{ fontSize: '0.88rem', fontWeight: 700, color: '#334155', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FiFilter size={16} className="text-primary" /> Metas de Destino (Mesma Natureza)
                    </h6>
                    <span style={{ fontSize: '0.76rem', color: '#059669', fontWeight: 600 }}>
                      ✓ Filtradas estritamente por {getGoalTypeLabel(copyRefModal.sourceGoal.goal_type, copyRefModal.sourceGoal.goal_subtype)} • {getGoalClassificationLabel(copyRefModal.sourceGoal.goal_classification)}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary text-white" style={{ fontSize: '0.78rem', padding: '5px 10px' }}>
                      {copyRefModal.targetGoalIds.length} selecionadas de {copyRefModal.candidates.length}
                    </span>
                    <button
                      type="button"
                      className="btn-modern btn-modern-light btn-modern-sm"
                      onClick={toggleSelectAllCopyTargets}
                      disabled={copyRefModal.candidates.length === 0}
                      style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                    >
                      {copyRefModal.candidates.length > 0 && copyRefModal.candidates.every(c => copyRefModal.targetGoalIds.includes(c.id))
                        ? 'Desmarcar Todas'
                        : 'Selecionar Todas'}
                    </button>
                  </div>
                </div>

                {/* Filtros para buscar metas de destino */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  padding: '12px 14px',
                  background: '#f1f5f9',
                  borderRadius: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{ width: '130px' }}>
                    <select
                      className="form-control-modern form-control-modern-sm w-100"
                      value={copyRefModal.filterYear}
                      onChange={(e) => handleCopyModalFilterChange('filterYear', e.target.value)}
                      title="Ano de destino"
                      style={{ height: '36px' }}
                    >
                      <option value="">Todos os anos</option>
                      {(() => {
                        const curY = new Date().getFullYear();
                        const yearSet = new Set([curY + 1, curY, curY - 1, curY - 2, curY - 3, ...goals.map(g => Number(g.reference_year)).filter(Boolean)]);
                        return Array.from(yearSet).sort((a, b) => b - a).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ));
                      })()}
                    </select>
                  </div>

                  <div style={{ width: '140px' }}>
                    <select
                      className="form-control-modern form-control-modern-sm w-100"
                      value={copyRefModal.filterMonth}
                      onChange={(e) => handleCopyModalFilterChange('filterMonth', e.target.value)}
                      title="Mês de destino"
                      style={{ height: '36px' }}
                    >
                      <option value="">Todos os meses</option>
                      {MESES.map(m => (
                        <option key={m.v} value={m.v}>{m.n}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ width: '220px', position: 'relative', zIndex: 50 }}>
                    <MultiSelectSearch
                      label=""
                      placeholder="Filial destino..."
                      value={copyRefModal.filterBranch}
                      onChange={(val) => handleCopyModalFilterChange('filterBranch', val)}
                      fetchData={filterDataService.getFiliais}
                      badgeColor="#10b981"
                      showCodePrefix
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: '180px', position: 'relative' }}>
                    <input
                      type="text"
                      className="form-control-modern form-control-modern-sm w-100"
                      placeholder="Buscar por vendedor, funcionário ou filial..."
                      value={copyRefModal.searchTerm}
                      onChange={(e) => handleCopyModalSearchChange(e.target.value)}
                      style={{ height: '36px', paddingRight: '32px' }}
                    />
                    {copyRefModal.searchTerm && (
                      <button
                        type="button"
                        onClick={() => handleCopyModalSearchChange('')}
                        style={{
                          position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                          border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8'
                        }}
                      >
                        <FiX size={14} />
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn-modern btn-modern-light btn-modern-sm"
                    onClick={() => fetchCopyCandidates(copyRefModal.sourceGoal, {
                      filterYear: copyRefModal.filterYear,
                      filterMonth: copyRefModal.filterMonth,
                      filterBranch: copyRefModal.filterBranch,
                      searchTerm: copyRefModal.searchTerm
                    })}
                    title="Recarregar metas compatíveis"
                    style={{ height: '36px', padding: '0 12px' }}
                  >
                    <FiRefreshCw size={14} className={copyRefModal.loading ? 'spin-ico' : ''} />
                  </button>
                </div>

                {/* Tabela de metas candidatas */}
                <div style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  maxHeight: '280px',
                  overflowY: 'auto',
                  background: 'white'
                }}>
                  {copyRefModal.loading ? (
                    <div style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                      <FiRefreshCw size={24} className="spin-ico text-primary mb-2 d-block mx-auto" />
                      <span style={{ fontSize: '0.88rem' }}>Carregando metas correspondentes da mesma natureza...</span>
                    </div>
                  ) : copyRefModal.candidates.length === 0 ? (
                    <div style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
                      <FiAlertTriangle size={24} className="mb-2 d-block mx-auto text-warning opacity-75" />
                      <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#475569' }}>
                        Nenhuma meta compatível encontrada para os filtros selecionados.
                      </span>
                      <p style={{ margin: '4px 0 0', fontSize: '0.78rem' }}>
                        Tente ajustar o ano, mês ou filial acima para visualizar outras metas da mesma natureza.
                      </p>
                    </div>
                  ) : (
                    <table className="table-modern w-100" style={{ margin: 0, fontSize: '0.82rem' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>
                        <tr>
                          <th style={{ width: '40px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={copyRefModal.candidates.length > 0 && copyRefModal.candidates.every(c => copyRefModal.targetGoalIds.includes(c.id))}
                              onChange={toggleSelectAllCopyTargets}
                            />
                          </th>
                          <th style={{ width: '60px' }}>ID</th>
                          <th>Beneficiário / Alvo</th>
                          <th>Filial</th>
                          <th>Período</th>
                          <th>Meta Atual</th>
                          <th>Filtros Existentes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {copyRefModal.candidates.map(candidate => {
                          const isChecked = copyRefModal.targetGoalIds.includes(candidate.id);
                          return (
                            <tr
                              key={candidate.id}
                              onClick={() => toggleCopyTargetGoal(candidate.id)}
                              style={{
                                cursor: 'pointer',
                                background: isChecked ? '#f0fdf4' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                            >
                              <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleCopyTargetGoal(candidate.id)}
                                />
                              </td>
                              <td><code style={{ fontSize: '0.74rem' }}>{candidate.id}</code></td>
                              <td>
                                <strong style={{ color: '#1e293b' }}>
                                  {candidate.vendor_name || candidate.employee_name || (candidate.branch_name ? candidate.branch_name : (candidate.branch_code ? `Filial ${candidate.branch_code}` : 'Meta Geral'))}
                                </strong>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {candidate.branch_code ? `Filial ${candidate.branch_code}` : '-'}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">{periodLabel(candidate)}</span>
                              </td>
                              <td className="fw-semibold">
                                {isQuantitySubtype(candidate.goal_subtype)
                                  ? `${Number(candidate.target_quantity || 0).toLocaleString('pt-BR')} un`
                                  : Number(candidate.target_amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </td>
                              <td style={{ maxWidth: '240px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <span className="text-muted small" title={resolveFilterNames(candidate)}>
                                  {resolveFilterNames(candidate) || 'Nenhum'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#fafafa',
              flexShrink: 0
            }}>
              <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                {copyRefModal.targetGoalIds.length > 0 ? (
                  <span>
                    Destino: <strong className="text-primary">{copyRefModal.targetGoalIds.length}</strong> meta(s) selecionada(s) receberão as configurações copiadas.
                  </span>
                ) : (
                  <span className="text-warning">
                    ⚠️ Selecione ao menos uma meta de destino na tabela acima.
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="btn-modern btn-modern-light"
                  onClick={() => setCopyRefModal(prev => ({ ...prev, open: false }))}
                  disabled={copyRefModal.applying}
                  style={{ fontSize: '0.85rem' }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-modern btn-modern-primary"
                  onClick={handleApplyCopyReferences}
                  disabled={copyRefModal.applying || copyRefModal.targetGoalIds.length === 0}
                  style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  {copyRefModal.applying ? (
                    <>
                      <FiRefreshCw size={14} className="spin-ico" />
                      Aplicando Cópia...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      Aplicar Cópia ({copyRefModal.targetGoalIds.length} metas)
                    </>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      <style>{`
        .spin-ico { animation: spin-ico 0.8s linear infinite; }
        @keyframes spin-ico { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .sortable-header {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .sortable-header:hover {
          background-color: #f1f5f9;
          color: #4338ca;
        }
      `}</style>
    </div>
  );
}

export default Metas;
